import React, { useEffect, useRef, useState } from 'react'
import { useSocialStore } from '../../../stores/social';
import { Input } from '../../ui/inputs/Input';
import { formatComptableMaisPasTrop, formatEuroValue, safeNumber } from '../../../services/function';
import { getPayrollSettings, getPayrollSettingsByCustomer } from '../../../api/payroll';
import { showToastError } from '../../../services/toast';
import { useParams } from 'react-router-dom';
import { grossUpSalaireGainFromNet } from './payrollCompute';

type InputMode = 'BRUT' | 'NET';

const getSalaireGainBrut = (prev: Record<string, any>): number => {
    if (prev.inputMode === 'NET') {
        return safeNumber(prev.salaireGainBrut);
    }
    return safeNumber(prev.salaireGain);
};

const Simulator = () => {
    const { formDataSimulator, setFormDataSimulator, annual, formData, formDataByCustomer, setFormData, setFormDataByCustomer, allData, setAllData } = useSocialStore();
    const { idCustomer } = useParams();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const netRefineAttemptsRef = useRef(0);

    useEffect(() => {
        if (!idCustomer) {
            setIsLoading(false);
            return;
        }
        
        setIsLoading(true);
        // Attendre que les deux appels API soient terminés avant de fusionner les données
        Promise.all([
          getPayrollSettings(),
          getPayrollSettingsByCustomer(Number(idCustomer))
        ])
          .then(([settingsResponse, customerResponse]) => {
            const settingsData = settingsResponse.data;
            const customerData = customerResponse.data;
            
            // Mettre à jour les états séparés
            setFormData(settingsData);
            setFormDataByCustomer(customerData);
            
            // Fusionner les données (les données customer écrasent les données settings)
            setAllData({ ...settingsData, ...customerData });
            setIsLoading(false);
          })
          .catch((err) => {
            showToastError(err.response?.data?.message || 'Erreur lors du chargement des paramètres');
            setIsLoading(false);
          });
      }, [idCustomer, setFormData, setFormDataByCustomer]);

    // Mettre à jour allData quand formData ou formDataByCustomer changent dans le store
    useEffect(() => {
        if (formData && Object.keys(formData).length > 0) {
            // Fusionner les données (les données customer écrasent les données settings)
            setAllData({ ...formData, ...formDataByCustomer });
        }
    }, [formData, formDataByCustomer]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let inputValue = e.target.value;
        let formattedValue: string | number = inputValue;
        
        // Normaliser les virgules en points pour le traitement interne
        // Mais préserver la valeur originale pour l'affichage si c'est une saisie en cours
        const normalizedValue = inputValue.replace(/,/g, '.');
        
        // Liste des champs avec suffixe "€" qui doivent être formatés
        const euroFields = [
            'salaireTaux', 'salaireGain', 'tauxHeuresSup125', 'salaireGainHeuresSup125',
            'tauxHeuresSup150', 'salaireGainHeuresSup150', 'primeAncienneteMontant',
            'bonusPrime', 'avantageEnNature', 'ccssEuro', 'ccssPartSalariale', 'ccssPartPatronale',
            'car', 'carPartSalariale', 'carPartPatronale', 'poleEmploi', 'poleEmploiPartSalariale',
            'poleEmploiPartPatronale', 'cmrcTaux', 'cmrcTauxPartSalariale', 'cmrcTauxPartPatronale',
            'cmrcTB', 'cmrcTBPartSalariale', 'cmrcTBPartPatronale', 'ccpbCongesFormations',
            'ccpbCongesFormationsPartSalariale', 'ccpbCongesFormationsPartPatronale',
            'ccpbPrevoyance', 'ccpbPrevoyancePartSalariale', 'ccpbPrevoyancePartPatronale',
            'prevoyanceT1CCSS', 'prevoyanceT1CCSSPartSalariale', 'prevoyanceT1CCSSPartPatronale',
            'prevoyanceTASSFR', 'prevoyanceTASSFRPartSalariale', 'prevoyanceTASSFRPartPatronale',
            'prevoyanceT2CCSS', 'prevoyanceT2CCSSPartSalariale', 'prevoyanceT2CCSSPartPatronale',
            'prevoyanceTB', 'prevoyanceTBPartSalariale', 'prevoyanceTBPartPatronale',
            'prevoyanceTC', 'prevoyanceTCPartSalariale', 'prevoyanceTCPartPatronale',
            'fraisDeSantePartSalariale', 'fraisDeSantePartPatronale', 'cotisationATPartSalariale',
            'cotisationATPartPatronale', 'taxeSurCotisationATPartPartronale', 'fondsSocial',
            'fondsSocialPartSalariale', 'taxeSurCotisationAT'
        ];

        // Liste des champs qui doivent être forcément négatifs
        const negativeFields = ['fraisDeSantePartSalariale', 'fraisDeSantePartPatronale'];
        
        // Si c'est un champ avec suffixe "€", formater la valeur
        if (euroFields.includes(e.target.name)) {
            if (inputValue === "" || inputValue === null || inputValue === undefined) {
                formattedValue = "";
            } else {
                // Accepter les virgules et les points, normaliser en point
                // Permettre la saisie en cours (ex: "12.", "12,")
                if (/^-?\d*[.,]?\d*$/.test(inputValue)) {
                    // Si la valeur se termine par un point ou une virgule, garder la valeur normalisée comme string
                    if (inputValue.endsWith('.') || inputValue.endsWith(',')) {
                        formattedValue = normalizedValue;
                    } else {
                        const numValue = parseFloat(normalizedValue);
                        if (!isNaN(numValue)) {
                            formattedValue = parseFloat(numValue.toFixed(2));
                            // Forcer la valeur à être négative pour les champs spécifiés
                            if (negativeFields.includes(e.target.name) && formattedValue > 0) {
                                formattedValue = -Math.abs(formattedValue);
                            }
                        } else if (inputValue === "-" || inputValue === "." || inputValue === "," || inputValue === "-." || inputValue === "-,") {
                            // Permettre la saisie en cours (signe moins, point ou virgule)
                            formattedValue = normalizedValue;
                        } else {
                            formattedValue = normalizedValue;
                        }
                    }
                } else {
                    formattedValue = inputValue;
                }
            }
        } else if (negativeFields.includes(e.target.name)) {
            // Si le champ doit être négatif mais n'est pas dans euroFields
            if (/^-?\d*[.,]?\d*$/.test(inputValue)) {
                if (inputValue.endsWith('.') || inputValue.endsWith(',')) {
                    formattedValue = normalizedValue;
                } else {
                    const numValue = parseFloat(normalizedValue);
                    if (!isNaN(numValue)) {
                        formattedValue = -Math.abs(parseFloat(numValue.toFixed(2)));
                    } else {
                        formattedValue = normalizedValue;
                    }
                }
            } else {
                formattedValue = inputValue;
            }
        } else {
            // Pour tous les autres champs numériques, permettre les nombres décimaux
            if (inputValue === "" || inputValue === null || inputValue === undefined) {
                formattedValue = "";
            } else {
                // Permettre la saisie de nombres décimaux avec virgule ou point
                // On accepte les valeurs qui sont des nombres valides ou en cours de saisie (ex: "12.", "12,", "12.5", "12,5")
                if (/^-?\d*[.,]?\d*$/.test(inputValue)) {
                    // Si la valeur se termine par un point ou une virgule, garder la valeur normalisée comme string
                    if (inputValue.endsWith('.') || inputValue.endsWith(',')) {
                        formattedValue = normalizedValue;
                    } else {
                        const numValue = parseFloat(normalizedValue);
                        if (!isNaN(numValue)) {
                            formattedValue = numValue;
                        } else if (inputValue === "-" || inputValue === "." || inputValue === "," || inputValue === "-." || inputValue === "-,") {
                            // Permettre la saisie en cours (signe moins, point ou virgule)
                            formattedValue = normalizedValue;
                        } else {
                            formattedValue = normalizedValue;
                        }
                    }
                } else {
                    // Si ce n'est pas un nombre valide, garder la valeur précédente ou vide
                    formattedValue = inputValue;
                }
            }
        }

        setFormDataSimulator((prev: any) => ({ ...prev, [e.target.name]: formattedValue}));
      };

    const handleInputModeChange = (mode: InputMode) => {
        setFormDataSimulator((prev: any) => ({
            ...prev,
            inputMode: mode,
            salaireGainBrut: mode === 'BRUT' ? safeNumber(prev.salaireGain) : prev.salaireGainBrut,
        }));
    };

    const inputMode: InputMode = formDataSimulator.inputMode === 'NET' ? 'NET' : 'BRUT';
    const isNetMode = inputMode === 'NET';

    // Mode BRUT : salaireGainBrut = saisie directe
    useEffect(() => {
        if (inputMode !== 'BRUT') return;

        setFormDataSimulator((prev: any) => {
            const gainBrut = safeNumber(prev.salaireGain);
            if (prev.salaireGainBrut === gainBrut) return prev;
            return { ...prev, salaireGainBrut: gainBrut };
        });
    }, [inputMode, formDataSimulator.salaireGain, setFormDataSimulator]);

    // Mode NET : gross-up synchrone (calcul en une passe, sans lire totalNet des useEffect)
    useEffect(() => {
        if (!allData || inputMode !== 'NET') return;

        const targetNet = safeNumber(formDataSimulator.salaireGain);
        if (
            formDataSimulator.salaireBaseHeures === "" ||
            formDataSimulator.salaireGain === "" ||
            !targetNet
        ) {
            setFormDataSimulator((prev: any) => {
                if (prev.salaireGainBrut === 0) return prev;
                return { ...prev, salaireGainBrut: 0 };
            });
            return;
        }

        netRefineAttemptsRef.current = 0;
        const brut = grossUpSalaireGainFromNet(targetNet, formDataSimulator, allData);
        setFormDataSimulator((prev: any) => {
            if (prev.salaireGainBrut === brut) return prev;
            return { ...prev, salaireGainBrut: brut };
        });
    }, [
        inputMode,
        allData,
        formDataSimulator.salaireGain,
        formDataSimulator.salaireBaseHeures,
        formDataSimulator.heuresSup125,
        formDataSimulator.heuresSup150,
        formDataSimulator.primeAnciennetePourcentage,
        formDataSimulator.bonusPrime,
        formDataSimulator.avantageEnNature,
        formDataSimulator.nbTitresRestaurant,
        formDataSimulator.montantSalarialTitresRestaurant,
        formDataSimulator.nbPaniers,
        formDataSimulator.montantSalarialPaniers,
        formDataSimulator.remboursementTransportPatronale,
        formDataSimulator.indemniteTeletravail,
        formDataSimulator.fraisDiversesPatronale,
        formDataSimulator.fraisDeSantePartSalariale,
        formDataSimulator.ccpbCongesFormationsPartSalariale,
        setFormDataSimulator,
    ]);

    // PREMIER BLOC : CALCULS DES GAINS ET TAUX
    // Champ ecouté : salaireBaseHeures et salaireGainBrut
    useEffect(() => {
      setFormDataSimulator((prev: any) => {
        const gainBrut = getSalaireGainBrut(prev);
        if (prev.salaireBaseHeures && gainBrut) {
          const taux = gainBrut / prev.salaireBaseHeures;
          return { ...prev, salaireTaux: parseFloat(taux.toFixed(2)) };
        } else {
          return { ...prev, salaireTaux: 0 };
        }
      });
    }, [formDataSimulator.salaireBaseHeures, formDataSimulator.salaireGain, formDataSimulator.salaireGainBrut, formDataSimulator.inputMode, setFormDataSimulator]);

    // Champ ecouté : heuresSup125, salaireTaux
    useEffect(() => {
      setFormDataSimulator((prev: any) => {
        if (prev.heuresSup125 && prev.salaireTaux) {
          // Calcul du taux des heures sup 125%
          const tauxHeuresSup125 = Number(prev.salaireTaux) * 1.25;
          const tauxHeuresSup125Rounded = parseFloat(tauxHeuresSup125.toFixed(2));
          
          // Calcul du gain des heures sup 125%
          const gain = Number(prev.heuresSup125) * tauxHeuresSup125Rounded;
          const gainRounded = parseFloat(gain.toFixed(2));
          
          // Mise à jour en une seule opération pour éviter les problèmes de synchronisation
          return { 
            ...prev, 
            tauxHeuresSup125: tauxHeuresSup125Rounded,
            salaireGainHeuresSup125: gainRounded
          };
        } else if (prev.heuresSup125 === "" || prev.heuresSup125 === null || prev.heuresSup125 === undefined) {
          // Réinitialiser les valeurs si heuresSup125 est vide
          return { 
            ...prev, 
            tauxHeuresSup125: 0,
            salaireGainHeuresSup125: 0
          };
        }
        return prev;
      });
    }, [formDataSimulator.heuresSup125, formDataSimulator.salaireTaux, setFormDataSimulator]);

    // Champ ecouté : heuresSup150, salaireTaux
    useEffect(() => {
      setFormDataSimulator((prev: any) => {
        if (prev?.heuresSup150 && prev?.salaireTaux) {
          // Calcul du taux des heures sup 150%
          const taux = Number(prev?.salaireTaux) * 1.5;
          const tauxRounded = parseFloat(taux.toFixed(2));

          // Calcul du gain des heures sup 150%
          const gain = Number(prev?.heuresSup150) * tauxRounded;
          const gainRounded = parseFloat(gain.toFixed(2));
          
          // Mise à jour en une seule opération pour éviter les problèmes de synchronisation
          return { 
            ...prev, 
            tauxHeuresSup150: tauxRounded,
            salaireGainHeuresSup150: gainRounded
          };
        } else {
          return { ...prev, tauxHeuresSup150: 0, salaireGainHeuresSup150: 0 };
        }
      });
    }, [formDataSimulator?.heuresSup150, formDataSimulator?.salaireTaux, setFormDataSimulator]);

    // Champ écoute : primeAnciennetePourcentage, salaireBaseHeures et salaireGainBrut
    useEffect(() => {
        setFormDataSimulator((prev: any) => {
          const gainBrut = getSalaireGainBrut(prev);
          if (prev.primeAnciennetePourcentage && prev.salaireBaseHeures && gainBrut) {
            const primeAnciennete = gainBrut * prev.primeAnciennetePourcentage / 100;
            return { ...prev, primeAncienneteMontant: parseFloat(primeAnciennete.toFixed(2)) };
          } else {
            return { ...prev, primeAncienneteMontant: 0 };
          }
        });
      }, [formDataSimulator.primeAnciennetePourcentage, formDataSimulator.salaireBaseHeures, formDataSimulator.salaireGain, formDataSimulator.salaireGainBrut, formDataSimulator.inputMode, setFormDataSimulator]);

    // Champ écoute : salaireBaseHeures, heuresSup125, heuresSup150, salaireGainBrut, salaireGainHeuresSup125, salaireGainHeuresSup150, primeAncienneteMontant, bonusPrime, avantageEnNature
    useEffect(() => {
        setFormDataSimulator((prev: any) => {
          const gainBrut = getSalaireGainBrut(prev);
          const totalGain = Number(gainBrut || 0) + Number(prev.salaireGainHeuresSup125 || 0) + Number(prev.salaireGainHeuresSup150 || 0) + Number(prev.primeAncienneteMontant || 0) + Number(prev.bonusPrime || 0) + Number(prev.avantageEnNature || 0);
          const totalHeures = Number(prev.salaireBaseHeures || 0) + Number(prev.heuresSup125 || 0) + Number(prev.heuresSup150 || 0);
          return { 
            ...prev, 
            totalGain: parseFloat(totalGain.toFixed(2)),
            totalHeures: totalHeures
          };
        });
      }, [formDataSimulator.salaireBaseHeures, formDataSimulator.heuresSup125, formDataSimulator.heuresSup150, formDataSimulator.salaireGain, formDataSimulator.salaireGainBrut, formDataSimulator.inputMode, formDataSimulator.salaireGainHeuresSup125, formDataSimulator.salaireGainHeuresSup150, formDataSimulator.primeAncienneteMontant, formDataSimulator.bonusPrime, formDataSimulator.avantageEnNature, setFormDataSimulator]);

    // DEUXIEME BLOC : CALCULS DES PART SALARIALE ET PATRONALE
    /*************** CCSS ***************/
    // Pour le champ ccssEuro
    useEffect(() => {
       if (!allData) return; // Ne pas exécuter si formData n'est pas chargé
       
       if(formDataSimulator.salaireBaseHeures === "" || formDataSimulator.salaireGain === "") {
           setFormDataSimulator((prev: any) => ({ ...prev, ccssEuro: 0}));
       } else {
        // Si le administrateur SAM est coché
        if(allData?.administrateurSAM) {
            setFormDataSimulator((prev: any) => ({ ...prev, ccssEuro: allData?.ccss__cgcs__amount }));
        } 
        // Si le gens de maison est coché
        if (allData?.CCSSHousehold) {
            let totalBrut = formDataSimulator.totalGain * 0.33;
            // Recupere le plus petit nombre entre le total brut et le plafond des gens de maison
            setFormDataSimulator((prev: any) => ({ ...prev, ccssEuro: Math.min(totalBrut, allData.ccss_gens_de_maison___254h_amount) }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, ccssEuro: Math.min(formDataSimulator.totalGain, allData?.ccss__cgcs__amount) }));
        }
       }
    },[formDataSimulator.salaireBaseHeures, formDataSimulator.salaireGain, allData?.administrateurSAM, allData?.CCSSHousehold, allData?.ccss__cgcs__amount, allData?.ccss_gens_de_maison___254h_amount, allData, formDataSimulator.totalGain])

    // Pour le champ ccssPartPatronale%
    useEffect(() => {
        if (!allData) return; // Ne pas exécuter si allData n'est pas chargé

        if (allData?.CCSSCGCS && allData?.ccss__cgcs__partPatronale !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, ccssPartPatronalePourcentage: allData?.ccss__cgcs__partPatronale }));
        } else if (allData?.CCSSReduced && allData?.ccss_taux_r_duit_partPatronale !== 0){
            setFormDataSimulator((prev: any) => ({ ...prev, ccssPartPatronalePourcentage: allData?.ccss_taux_r_duit_partPatronale }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, ccssPartPatronalePourcentage: 0 }));
        }
    }, [allData?.CCSSCGCS, allData?.CCSSReduced, allData?.ccss_taux_r_duit_partPatronale, allData?.ccss__cgcs__partPatronale, allData, formDataSimulator.salaireBaseHeures, formDataSimulator.salaireGain]);

    // Pour le champ ccssPartPatronale €
    useEffect(() => {
        if (!allData) return; // Ne pas exécuter si allData n'est pas chargé

        setFormDataSimulator((prev: any) => ({ ...prev, ccssPartPatronale: formDataSimulator.ccssEuro * (- formDataSimulator.ccssPartPatronalePourcentage / 100) }));
    }, [ formDataSimulator.ccssPartPatronalePourcentage, formDataSimulator.ccssEuro, allData]);

    /*************** CAR ***************/
    // Pour le champ carEuro
    useEffect(() => {
        if (!allData) return; // Ne pas exécuter si allData n'est pas chargé

        setFormDataSimulator((prev: any) => ({ ...prev, car: Math.min(formDataSimulator.totalGain, allData?.car_amount) }));
    }, [formDataSimulator.totalGain, allData?.car_amount, allData]);

    useEffect(() => {
        if (!allData) return; // Ne pas exécuter si allData n'est pas chargé
        if(allData?.car_partSalariale !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, carPartSalarialePourcentage: allData?.car_partSalariale }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, carPartSalarialePourcentage: 0 }));
        }
    }, [formDataSimulator.car, allData?.car_partSalariale, allData]);
    // Pour le champ carPartSalariale
    useEffect(() => {
        if (!allData) return; // Ne pas exécuter si allData n'est pas chargé

        setFormDataSimulator((prev: any) => ({ ...prev, carPartSalariale: formDataSimulator.car * (- allData?.car_partSalariale / 100) }));
    }, [formDataSimulator.car, allData?.car_partSalariale, allData]);

    useEffect(() => {
        if (!allData) return; // Ne pas exécuter si allData n'est pas chargé
        if(allData?.car_partPatronale !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, carPartPatronalePourcentage: allData?.car_partPatronale }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, carPartPatronalePourcentage: 0 }));
        }
    }, [formDataSimulator.car, allData?.car_partPatronale, allData]);

    // Pour le champ carPartPatronale
    useEffect(() => {
        if (!allData) return; // Ne pas exécuter si allData n'est pas chargé

        setFormDataSimulator((prev: any) => ({ ...prev, carPartPatronale: formDataSimulator.car * (- allData?.car_partPatronale / 100) }));
    }, [formDataSimulator.car, allData?.car_partPatronale, allData]);

    /*************** POLE EMPLOI ***************/
    // Pour le champ poleEmploiEuro
    useEffect(() => {
        if (!allData) return; // Ne pas exécuter si allData n'est pas chargé

        const total = allData?.france_travail_amount * (formDataSimulator.salaireBaseHeures / allData?.horaire_temps_plein_mon_gasque_hours);
        setFormDataSimulator((prev: any) => ({ ...prev, poleEmploi: Math.min(formDataSimulator.totalGain, total) }));
    }, [formDataSimulator.totalGain, allData?.france_travail_amount, allData?.horaire_temps_plein_mon_gasque_hours, allData, formDataSimulator.salaireBaseHeures]);

    // Pour le champ poleEmploiPartSalarialePourcentage
        useEffect(() => {
        if (!allData) return; // Ne pas exécuter si allData n'est pas chargé

        if(allData?.AffilAssChomage) {
           setFormDataSimulator((prev: any) => ({ ...prev, poleEmploiPartSalarialePourcentage: allData?.france_travail_partSalariale }));
        } else {
           setFormDataSimulator((prev: any) => ({ ...prev, poleEmploiPartSalarialePourcentage: 0 }));
        }
    }, [allData?.AffilAssChomage, allData?.france_travail_partSalariale, allData]);

    // Pour le champ poleEmploiPartSalariale
    useEffect(() => {
        if (!allData) return; // Ne pas exécuter si allData n'est pas chargé

        if(formDataSimulator.poleEmploiPartSalarialePourcentage !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, poleEmploiPartSalariale: formDataSimulator.poleEmploi * (- formDataSimulator.poleEmploiPartSalarialePourcentage / 100) }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, poleEmploiPartSalariale: 0 }));
        }
    }, [formDataSimulator.poleEmploi, formDataSimulator.poleEmploiPartSalarialePourcentage, allData]);

    // Pour le champ poleEmploiParPatronalePourcentage
    useEffect(() => {
        if (!allData) return; // Ne pas exécuter si allData n'est pas chargé

        if(allData?.AffilAssChomage) {
           setFormDataSimulator((prev: any) => ({ ...prev, poleEmploiPartPatronalePourcentage: allData?.france_travail_partPatronale }));
        } else {
           setFormDataSimulator((prev: any) => ({ ...prev, poleEmploiPartPatronalePourcentage: 0 }));
        }
    }, [formDataSimulator.poleEmploi, allData?.AffilAssChomage, allData?.france_travail_partPatronale, allData]);

    // Pour le champ poleEmploiPartPatronale
    useEffect(() => {
        if (!allData) return; // Ne pas exécuter si allData n'est pas chargé

        if(formDataSimulator.poleEmploiPartPatronalePourcentage !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, poleEmploiPartPatronale: formDataSimulator.poleEmploi * (- formDataSimulator.poleEmploiPartPatronalePourcentage / 100) }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, poleEmploiPartPatronale: 0 }));
        }
    }, [formDataSimulator.poleEmploi, formDataSimulator.poleEmploiPartPatronalePourcentage, allData]);

    /*************** CMRC TA ***************/
    // Pour le champ CMRC Taux
    useEffect(() => {
        if (!allData || !formDataSimulator.totalHeures || !formDataSimulator.salaireBaseHeures) return; // Ne pas exécuter si allData n'est pas chargé
        if(formDataSimulator.totalHeures >= allData?.horaire_temps_plein_cmrc_hours) {
            setFormDataSimulator((prev: any) => ({ ...prev, cmrcTaux: Math.min(formDataSimulator.totalGain, allData?.cmrc_ta_amount || 0)}));
        } else {
            const total = allData?.cmrc_ta_amount * (formDataSimulator.salaireBaseHeures / allData?.horaire_temps_plein_cmrc_hours);
            setFormDataSimulator((prev: any) => ({ ...prev, cmrcTaux: Math.min(formDataSimulator.totalGain, total) }));
        }
    }, [formDataSimulator.totalHeures, formDataSimulator.totalGain, formDataSimulator.salaireBaseHeures, allData?.horaire_temps_plein_cmrc_hours, allData?.cmrc_ta_amount, allData, setFormDataSimulator]);

    useEffect(() => {
        if (!allData) return; // Ne pas exécuter si allData n'est pas chargé
        if(allData?.cmrc_ta_partSalariale !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, cmrcTauxPartSalarialePourcentage: allData?.cmrc_ta_partSalariale }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, cmrcTauxPartSalarialePourcentage: 0 }));
        }
    }, [formDataSimulator.cmrcTaux, allData?.cmrc_ta_partSalariale, allData]);

    // Pour le champ cmrcTauxPartSalariale
    useEffect(() => {
        if (!allData) return; // Ne pas exécuter si allData n'est pas chargé

        setFormDataSimulator((prev: any) => ({ ...prev, cmrcTauxPartSalariale: formDataSimulator.cmrcTaux * (- allData?.cmrc_ta_partSalariale / 100) }));
    }, [formDataSimulator.cmrcTaux, allData?.cmrc_ta_partSalariale, allData]);

    useEffect(() => {
        if (!allData) return; // Ne pas exécuter si allData n'est pas chargé
        if(allData?.cmrc_ta_partPatronale !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, cmrcTauxPartPatronalePourcentage: allData?.cmrc_ta_partPatronale }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, cmrcTauxPartPatronalePourcentage: 0 }));
        }
    }, [formDataSimulator.cmrcTaux, allData?.cmrc_ta_partPatronale, allData]);

    // Pour le champ cmrcTauxPartPatronale
    useEffect(() => {
        if (!allData) return; // Ne pas exécuter si allData n'est pas chargé

        setFormDataSimulator((prev: any) => ({ ...prev, cmrcTauxPartPatronale: formDataSimulator.cmrcTaux * (- allData?.cmrc_ta_partPatronale / 100) }));
    }, [formDataSimulator.cmrcTaux, allData?.cmrc_ta_partPatronale, allData]);

    /*************** CMRC TB ***************/
    // Pour le champ CMRC TB
    useEffect(() => {
       if (!allData) return; // Ne pas exécuter si allData n'est pas chargé
       
       if(formDataSimulator.totalHeures >= allData?.horaire_temps_plein_cmrc_hours) {
         if(formDataSimulator.totalGain > (allData?.cmrc_ta_amount || 0) * 8) {
            setFormDataSimulator((prev: any) => ({ ...prev, cmrcTB: allData?.cmrc_tb_amount }));
         } else {
            const total = formDataSimulator.totalGain - (allData?.cmrc_ta_amount || 0);
            setFormDataSimulator((prev: any) => ({ ...prev, cmrcTB: Math.max(0, total) }));
         }
       } else {
         const total = (allData?.cmrc_ta_amount || 0) * 8 * (formDataSimulator.salaireBaseHeures / (allData?.horaire_temps_plein_cmrc_hours ));
         if(formDataSimulator.totalGain > total) {
            setFormDataSimulator((prev: any) => ({ ...prev, cmrcTB: (allData?.cmrc_tb_amount) * (formDataSimulator.salaireBaseHeures / (allData?.horaire_temps_plein_cmrc_hours )) }));
         } else {
            const total2 = formDataSimulator.totalGain - (allData?.cmrc_ta_amount || 0) * (formDataSimulator.salaireBaseHeures / allData?.horaire_temps_plein_cmrc_hours );
            setFormDataSimulator((prev: any) => ({ ...prev, cmrcTB: Math.max(0, total2) }));
         }
       }
    }, [formDataSimulator.totalHeures, allData?.cmrc_ta_amount, allData?.cmrc_tb_amount, formDataSimulator.totalGain, formDataSimulator.salaireBaseHeures, allData?.horaire_temps_plein_cmrc_hours, allData]);

    useEffect(() => {
        if (!allData) return; // Ne pas exécuter si allData n'est pas chargé
        if(allData?.cmrc_tb_partSalariale !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, cmrcTBPartSalarialePourcentage: allData?.cmrc_tb_partSalariale }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, cmrcTBPartSalarialePourcentage: 0 }));
        }
    }, [formDataSimulator.cmrcTB, allData?.cmrc_tb_partSalariale, allData]);

    
    // Pour le champ cmrcTBPartSalariale
    useEffect(() => {
        setFormDataSimulator((prev: any) => ({ ...prev, cmrcTBPartSalariale: formDataSimulator.cmrcTB * (- allData?.cmrc_tb_partSalariale / 100) }));
    }, [formDataSimulator.cmrcTB, allData?.cmrc_tb_partSalariale, allData]);
    
    useEffect(() => {
        if (!allData) return; // Ne pas exécuter si allData n'est pas chargé
        if(allData?.cmrc_tb_partPatronale !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, cmrcTBPartPatronalePourcentage: allData?.cmrc_tb_partPatronale }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, cmrcTBPartPatronalePourcentage: 0 }));
        }
    }, [formDataSimulator.cmrcTB, allData?.cmrc_tb_partPatronale, allData]);

    // Pour le champ cmrcTBPartPatronale
    useEffect(() => {
        setFormDataSimulator((prev: any) => ({ ...prev, cmrcTBPartPatronale: formDataSimulator.cmrcTB * (- allData?.cmrc_tb_partPatronale / 100) }));
    }, [formDataSimulator.cmrcTB, allData?.cmrc_tb_partPatronale, allData]);

    /*************** CCPB CONGES + FORMATIONS ***************/
    // Pour le champ ccpbCongesFormations
    useEffect(() => {
        setFormDataSimulator((prev: any) => ({ ...prev, ccpbCongesFormations: formDataSimulator.totalGain }));
    }, [formDataSimulator.totalGain, allData]);

    // Pour le champ ccpbCongesFormationsPartPatronalePourcentage
        useEffect(() => {
        if(allData?.AffilCCPB_EtamCadres || allData?.AffilCCPB_Ouvriers ) {
            setFormDataSimulator((prev: any) => ({ ...prev, ccpbCongesFormationsPartPatronalePourcentage: allData?.ccpb_cong_s___formation_partPatronale }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, ccpbCongesFormationsPartPatronalePourcentage: 0 }));
        }
    }, [allData?.ccpb_cong_s___formation_partPatronale, allData]);

    // Pour le champ ccpbCongesFormationsPartPatronale
    useEffect(() => {
        if(formDataSimulator.ccpbCongesFormationsPartPatronalePourcentage !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, ccpbCongesFormationsPartPatronale: formDataSimulator.ccpbCongesFormations * (- formDataSimulator.ccpbCongesFormationsPartPatronalePourcentage / 100) }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, ccpbCongesFormationsPartPatronale: 0 }));
        }
    }, [formDataSimulator.ccpbCongesFormationsPartPatronalePourcentage, allData?.ccpb_cong_s___formation_partPatronale, allData, formDataSimulator.ccpbCongesFormations]);

    /*************** CCPB PREVOYANCE ***************/
    // Pour le champ ccpbPrevoyance
    useEffect(() => {
        setFormDataSimulator((prev: any) => ({ ...prev, ccpbPrevoyance: formDataSimulator.totalGain }));
    }, [formDataSimulator.totalGain, allData]);

    // Pour le champ ccpbPrevoyancePartSalarialePourcentage
    useEffect(() => {
        if(allData?.AffilCCPB_Ouvriers && allData?.ccpb_pr_voyance_partSalariale !== 0 ) {
            setFormDataSimulator((prev: any) => ({ ...prev, ccpbPrevoyancePartSalarialePourcentage: allData?.ccpb_pr_voyance_partSalariale }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, ccpbPrevoyancePartSalarialePourcentage: 0 }));
        }
    }, [allData?.AffilCCPB_Ouvriers, allData?.ccpb_pr_voyance_partSalariale, allData]);

    // Pour le champ ccpbPrevoyancePartSalariale
    useEffect(() => {
        if(formDataSimulator.ccpbPrevoyancePartSalarialePourcentage !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, ccpbPrevoyancePartSalariale: formDataSimulator.ccpbPrevoyance * (- formDataSimulator.ccpbPrevoyancePartSalarialePourcentage / 100) }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, ccpbPrevoyancePartSalariale: 0 }));
        }
    }, [formDataSimulator.ccpbPrevoyance, formDataSimulator.ccpbPrevoyancePartSalarialePourcentage, allData]);

    // Pour le champ ccpbPrevoyancePartPatronalePourcentage
    useEffect(() => {
        if(allData?.AffilCCPB_Ouvriers) {
            setFormDataSimulator((prev: any) => ({ ...prev, ccpbPrevoyancePartPatronalePourcentage: allData?.ccpb_pr_voyance_partPatronale }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, ccpbPrevoyancePartPatronalePourcentage: 0 }));
        }
    }, [allData?.AffilCCPB_Ouvriers, allData?.ccpb_pr_voyance_partPatronale, allData]);

    // Pour le champ ccpbPrevoyancePartPatronale
    useEffect(() => {
        if(formDataSimulator.ccpbPrevoyancePartPatronalePourcentage !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, ccpbPrevoyancePartPatronale: formDataSimulator.ccpbPrevoyance * (- formDataSimulator.ccpbPrevoyancePartPatronalePourcentage / 100) }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, ccpbPrevoyancePartPatronale: 0 }));
        }
    }, [formDataSimulator.ccpbPrevoyance, formDataSimulator.ccpbPrevoyancePartPatronalePourcentage, allData]);

    /*************** PREVOYANCE T1 CCSS ***************/
    // Pour le champ prevoyanceT1CCSS
    useEffect(() => {
        if (!allData) return; // Ne pas exécuter si allData n'est pas chargé

        if(allData?.PrevoyanceT1_CCSS) {
            const total = allData?.ccss__cgcs__amount * (formDataSimulator.salaireBaseHeures / allData?.horaire_temps_plein_mon_gasque_hours);
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceT1CCSS: Math.min(formDataSimulator.totalGain, total)}));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceT1CCSS: Math.min(formDataSimulator.totalGain, allData?.ccss__cgcs__amount)}));
        }
    },[allData?.Affiliation, allData?.ccss__cgcs__amount, formDataSimulator.salaireBaseHeures, formDataSimulator.totalGain, allData?.PrevoyanceT1_CCSS, allData, formDataSimulator.totalGain])

    // Pour le champ prevoyanceT1CCSSPartSalarialePourcentage
    useEffect(() => {
        if (!allData) return; // Ne pas exécuter si allData n'est pas chargé

        if(allData?.prevoyanceT1_PartSalariale !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceT1CCSSPartSalarialePourcentage: allData?.prevoyanceT1_PartSalariale }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceT1CCSSPartSalarialePourcentage: 0 }));
        }
    }, [formDataSimulator.prevoyanceT1CCSS, allData?.prevoyanceT1_PartSalariale, allData]);

    // Pour le champ prevoyanceT1CCSSPartSalariale
    useEffect(() => {
        if (!allData) return; // Ne pas exécuter si allData n'est pas chargé

        if(formDataSimulator.prevoyanceT1CCSSPartSalarialePourcentage !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceT1CCSSPartSalariale: formDataSimulator.prevoyanceT1CCSS * (- formDataSimulator.prevoyanceT1CCSSPartSalarialePourcentage / 100) }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceT1CCSSPartSalariale: 0 }));
        }
    }, [formDataSimulator.prevoyanceT1CCSS, formDataSimulator.prevoyanceT1CCSSPartSalarialePourcentage, allData]);

    // Pour le champ prevoyanceT1CCSSPartPatronalePourcentage
    useEffect(() => {
        if (!allData) return; // Ne pas exécuter si allData n'est pas chargé

        if(allData?.prevoyanceT1_PartPatronale !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceT1CCSSPartPatronalePourcentage: allData?.prevoyanceT1_PartPatronale }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceT1CCSSPartPatronalePourcentage: 0 }));
        }
    }, [formDataSimulator.prevoyanceT1CCSS, allData?.prevoyanceT1_PartPatronale, allData, setFormDataSimulator ]);

    // Pour le champ prevoyanceT1CCSSPartPatronale
    useEffect(() => {
        if (!allData) return; // Ne pas exécuter si allData n'est pas chargé

        if(formDataSimulator.prevoyanceT1CCSSPartPatronalePourcentage !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceT1CCSSPartPatronale: formDataSimulator.prevoyanceT1CCSS * (- formDataSimulator.prevoyanceT1CCSSPartPatronalePourcentage / 100) }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceT1CCSSPartPatronale: 0 }));
        }
    }, [formDataSimulator.prevoyanceT1CCSS, formDataSimulator.prevoyanceT1CCSSPartPatronalePourcentage, allData, setFormDataSimulator ]);

    /*************** PREVOYANCE TA SS FR ***************/
    // Pour le champ prevoyanceTASSFR
    useEffect(() => {
        const total = allData?.plafond_ss_francais_amount * (formDataSimulator.salaireBaseHeures / allData?.horaire_temps_plein_mon_gasque_hours);
        setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceTASSFR: Math.min(formDataSimulator.totalGain, total) }));
    }, [formDataSimulator.totalGain, formDataSimulator.salaireBaseHeures, allData?.plafond_ss_francais_amount, allData?.horaire_temps_plein_mon_gasque_hours, allData]);

    // Pour le champ prevoyanceTASSFRPartSalarialePourcentage
    useEffect(() => {
        if(allData?.prevoyanceTA_PartSalariale !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceTASSFRPartSalarialePourcentage: allData?.prevoyanceTA_PartSalariale }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceTASSFRPartSalarialePourcentage: 0 }));
        }
    }, [formDataSimulator.prevoyanceTASSFR, allData?.prevoyanceTA_PartSalariale, allData]);

    // Pour le champ prevoyanceTASSFRPartSalariale
    useEffect(() => {
        if(formDataSimulator.prevoyanceTASSFRPartSalarialePourcentage !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceTASSFRPartSalariale: formDataSimulator.prevoyanceTASSFR * (- formDataSimulator.prevoyanceTASSFRPartSalarialePourcentage / 100) }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceTASSFRPartSalariale: 0 }));
        }
    }, [formDataSimulator.prevoyanceTASSFR, formDataSimulator.prevoyanceTASSFRPartSalarialePourcentage, allData]);

    // Pour le champ prevoyanceTASSFRPartPatronalePourcentage
    useEffect(() => {
        if(allData?.prevoyanceTA_PartPatronale !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceTASSFRPartPatronalePourcentage: allData?.prevoyanceTA_PartPatronale }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceTASSFRPartPatronalePourcentage: 0 }));
        }
    }, [formDataSimulator.prevoyanceTASSFR, allData?.prevoyanceTA_PartPatronale, allData]);

    // Pour le champ prevoyanceTSSFRPartPatronale
    useEffect(() => {
        if(formDataSimulator.prevoyanceTASSFRPartPatronalePourcentage !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceTASSFRPartPatronale: formDataSimulator.prevoyanceTASSFR * (- formDataSimulator.prevoyanceTASSFRPartPatronalePourcentage / 100) }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceTASSFRPartPatronale: 0 }));
        }
    }, [formDataSimulator.prevoyanceTASSFR, formDataSimulator.prevoyanceTASSFRPartPatronalePourcentage, allData]);

    /*************** PREVOYANCE T2 CCSS ***************/
    // Pour le champ prevoyanceT2CCSS
    useEffect(() => {
        if (!allData) return; // Ne pas exécuter si allData n'est pas chargé
        const total = (allData?.plafond_ss_francais_amount || 0) * 4 * (formDataSimulator.salaireBaseHeures / (allData?.horaire_temps_plein_mon_gasque_hours || 1));
        if(allData?.PrevoyanceT1_CCSS) {
            if(formDataSimulator.totalGain > total) {
                setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceT2CCSS: (allData?.prevoyanceT2_Amount || 0) * (formDataSimulator.salaireBaseHeures / (allData?.horaire_temps_plein_mon_gasque_hours || 1)) }));
            } else {
                const total = formDataSimulator.totalGain - (allData?.prevoyanceT1_Amount || 0) * (formDataSimulator.salaireBaseHeures / (allData?.horaire_temps_plein_mon_gasque_hours || 1));
                setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceT2CCSS: Math.max(0, total) }));
            }
        } else {
            if(formDataSimulator.totalGain > total) {
                setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceT2CCSS: allData?.prevoyanceT2_Amount || 0 }));
            } else {
                const calculatedValue = Math.max(0, formDataSimulator.totalGain - (allData?.prevoyanceT1_Amount || 0));
                setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceT2CCSS: calculatedValue }));
            }
        }
    }, [formDataSimulator.totalGain, allData?.Affiliation, allData?.prevoyanceT1_Amount, allData?.prevoyanceT2_Amount, allData?.plafond_ss_francais_amount, allData?.horaire_temps_plein_mon_gasque_hours, formDataSimulator.salaireBaseHeures, allData]);

    // Pour le champ prevoyanceT2CCSSPartSalarialePourcentage
    useEffect(() => {
        if(allData?.prevoyanceT2_PartSalariale !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceT2CCSSPartSalarialePourcentage: allData?.prevoyanceT2_PartSalariale }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceT2CCSSPartSalarialePourcentage: 0 }));
        }
    }, [formDataSimulator.prevoyanceT2CCSS, allData?.prevoyanceT2_PartSalariale, allData]);

    // Pour le champ prevoyanceT2CCSSPartSalariale
    useEffect(() => {
        if(formDataSimulator.prevoyanceT2CCSSPartSalarialePourcentage !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceT2CCSSPartSalariale: formDataSimulator.prevoyanceT2CCSS * (- formDataSimulator.prevoyanceT2CCSSPartSalarialePourcentage / 100) }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceT2CCSSPartSalariale: 0 }));
        }
    }, [formDataSimulator.prevoyanceT2CCSS, formDataSimulator.prevoyanceT2CCSSPartSalarialePourcentage, allData]);

    // Pour le champ prevoyanceT2CCSSPartPatronalePourcentage
    useEffect(() => {
        if(allData?.prevoyanceT2_PartPatronale !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceT2CCSSPartPatronalePourcentage: allData?.prevoyanceT2_PartPatronale }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceT2CCSSPartPatronalePourcentage: 0 }));
        }
    }, [formDataSimulator.prevoyanceT2CCSS, allData?.prevoyanceT2_PartPatronale, allData]);

    // Pour le champ prevoyanceT2CCSSPartPatronale
    useEffect(() => {
        if(formDataSimulator.prevoyanceT2CCSSPartPatronalePourcentage !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceT2CCSSPartPatronale: formDataSimulator.prevoyanceT2CCSS * (- formDataSimulator.prevoyanceT2CCSSPartPatronalePourcentage / 100) }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceT2CCSSPartPatronale: 0 }));
        }
    }, [formDataSimulator.prevoyanceT2CCSS, formDataSimulator.prevoyanceT2CCSSPartPatronalePourcentage, allData]);

    /*************** PREVOYANCE TB ***************/
    // Pour le champ prevoyanceTB
    useEffect(() => {
        const total = allData?.plafond_ss_francais_amount * 4 * (formDataSimulator.salaireBaseHeures / allData?.horaire_temps_plein_mon_gasque_hours);
        if(formDataSimulator.totalGain > total) {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceTB: allData?.prevoyanceTB_Amount * (formDataSimulator.salaireBaseHeures / allData?.horaire_temps_plein_mon_gasque_hours) }));
        } else {
            const total = formDataSimulator.totalGain - allData?.plafond_ss_francais_amount * (formDataSimulator.salaireBaseHeures / allData?.horaire_temps_plein_mon_gasque_hours);
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceTB: Math.max(0, total) }));
        }
    }, [formDataSimulator.totalGain, allData?.prevoyanceTB_Amount, allData?.plafond_ss_francais_amount, allData?.horaire_temps_plein_mon_gasque_hours, formDataSimulator.salaireBaseHeures, allData]);

    // Pour le champ prevoyanceTBPartSalarialePourcentage
    useEffect(() => {
        if(allData?.prevoyanceTB_PartSalariale !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceTBPartSalarialePourcentage: allData?.prevoyanceTB_PartSalariale }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceTBPartSalarialePourcentage: 0 }));
        }
    }, [formDataSimulator.prevoyanceTB, allData?.prevoyanceTB_PartSalariale, allData]);

    // Pour le champ prevoyanceTBPartSalariale
    useEffect(() => {
        if(formDataSimulator.prevoyanceTBPartSalarialePourcentage !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceTBPartSalariale: formDataSimulator.prevoyanceTB * (- formDataSimulator.prevoyanceTBPartSalarialePourcentage / 100) }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceTBPartSalariale: 0 }));
        }
    }, [formDataSimulator.prevoyanceTB, formDataSimulator.prevoyanceTBPartSalarialePourcentage, allData]);

    // Pour le champ prevoyanceTBPartPatronalePourcentage
    useEffect(() => {
        if(allData?.prevoyanceTB_PartPatronale !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceTBPartPatronalePourcentage: allData?.prevoyanceTB_PartPatronale }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceTBPartPatronalePourcentage: 0 }));
        }
    }, [formDataSimulator.prevoyanceTB, allData?.prevoyanceTB_PartPatronale, allData]);

    // Pour le champ prevoyanceTBPartPatronale
    useEffect(() => {
        if(formDataSimulator.prevoyanceTBPartPatronalePourcentage !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceTBPartPatronale: formDataSimulator.prevoyanceTB * (- formDataSimulator.prevoyanceTBPartPatronalePourcentage / 100) }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceTBPartPatronale: 0 }));
        }
    }, [formDataSimulator.prevoyanceTB, formDataSimulator.prevoyanceTBPartPatronalePourcentage, allData]);

    /*************** PREVOYANCE TC ***************/
    // Pour le champ prevoyanceTC
    useEffect(() => {
        if (!allData) return; // Ne pas exécuter si allData n'est pas chargé
        
        const total8 = (allData?.plafond_ss_francais_amount || 0) * 8 * (formDataSimulator.salaireBaseHeures / (allData?.horaire_temps_plein_mon_gasque_hours || 1));
        if(formDataSimulator.totalGain > total8) {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceTC: (allData?.prevoyanceTC_Amount || 0) * (formDataSimulator.salaireBaseHeures / (allData?.horaire_temps_plein_mon_gasque_hours || 1)) }));
        } else {
            const total4 = (allData?.plafond_ss_francais_amount || 0) * 4 * (formDataSimulator.salaireBaseHeures / (allData?.horaire_temps_plein_mon_gasque_hours || 1));
            if(formDataSimulator.totalGain > total4) {
                const calcul = formDataSimulator.totalGain - (allData?.prevoyanceTA_Amount || 0) * 4 * (formDataSimulator.salaireBaseHeures / (allData?.horaire_temps_plein_mon_gasque_hours || 1));
                setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceTC: calcul}))
            } else {
                if(formDataSimulator.totalGain > (allData?.plafond_ss_francais_amount || 0) * (formDataSimulator.salaireBaseHeures / (allData?.horaire_temps_plein_mon_gasque_hours || 1))) {
                    setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceTC: 0}))
                } else {
                    setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceTC: 0 }));
                }
            }
        }
    }, [formDataSimulator.totalGain, allData?.prevoyanceTC_PartSalariale, allData, allData?.prevoyanceTC_Amount , formDataSimulator.salaireBaseHeures, allData?.horaire_temps_plein_mon_gasque_hours, allData?.plafond_ss_francais_amount]);

    // Pour le champ prevoyanceTCPartSalarialePourcentage
    useEffect(() => {
        if(allData?.prevoyanceTC_PartSalariale !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceTCPartSalarialePourcentage: allData?.prevoyanceTC_PartSalariale }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceTCPartSalarialePourcentage: 0 }));
        }
    }, [formDataSimulator.prevoyanceTC, allData?.prevoyanceTC_PartSalariale, allData]);

    // Pour le champ prevoyanceTCPartSalariale
    useEffect(() => {
        if(formDataSimulator.prevoyanceTCPartSalarialePourcentage !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceTCPartSalariale: formDataSimulator.prevoyanceTC * (- formDataSimulator.prevoyanceTCPartSalarialePourcentage / 100) }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceTCPartSalariale: 0 }));
        }
    }, [formDataSimulator.prevoyanceTC, formDataSimulator.prevoyanceTCPartSalarialePourcentage, allData]);

    // Pour le champ prevoyanceTCPartPatronalePourcentage
    useEffect(() => {
        if(allData?.prevoyanceTC_PartPatronale !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceTCPartPatronalePourcentage: allData?.prevoyanceTC_PartPatronale }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceTCPartPatronalePourcentage: 0 }));
        }
    }, [formDataSimulator.prevoyanceTC, allData?.prevoyanceTC_PartPatronale, allData]);

    // Pour le champ prevoyanceTCPartPatronale
    useEffect(() => {
        if(formDataSimulator.prevoyanceTCPartPatronalePourcentage !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceTCPartPatronale: formDataSimulator.prevoyanceTC * (- formDataSimulator.prevoyanceTCPartPatronalePourcentage / 100) }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, prevoyanceTCPartPatronale: 0 }));
        }
    }, [formDataSimulator.prevoyanceTC, formDataSimulator.prevoyanceTCPartPatronalePourcentage, allData]);

    /*************** FRAIS DE SANTÉ ***************/
    // SAISIE UNIQUEMENT PAR L UTILISATEUR

    /*************** COTISATIONS AT ***************/
    // Pour le champ cotisationAT
    useEffect(() => {
        setFormDataSimulator((prev: any) => ({ ...prev, cotisationAT: formDataSimulator.totalGain}));
    }, [formDataSimulator.totalGain, allData]);

    // Pour le champ cotisationATPartSalarialePourcentage
    useEffect(() => {
        if(allData?.cotisationAT_PartSalariale !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, cotisationATPartSalarialePourcentage: allData?.cotisationAT_PartSalariale }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, cotisationATPartSalarialePourcentage: 0 }));
        }
    }, [formDataSimulator.cotisationAT, allData?.cotisationAT_PartSalariale, allData]);

    // Pour le champ cotisationATPartSalariale
    useEffect(() => {
        if(formDataSimulator.cotisationATPartSalarialePourcentage !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, cotisationATPartSalariale: formDataSimulator.cotisationAT * (- formDataSimulator.cotisationATPartSalarialePourcentage / 100) }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, cotisationATPartSalariale: 0 }));
        }
    }, [formDataSimulator.cotisationAT, formDataSimulator.cotisationATPartSalarialePourcentage, allData]);

    // Pour le champ cotisationATPartPatronalePourcentage
    useEffect(() => {
        if (!allData) return; // Ne pas exécuter si allData n'est pas chargé
        if(allData?.tauxAT !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, cotisationATPartPatronalePourcentage: allData?.tauxAT }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, cotisationATPartPatronalePourcentage: 0 }));
        }
    }, [formDataSimulator.cotisationAT, allData?.tauxAT, allData]);

    // Pour le champ cotisationATPartPatronale
    useEffect(() => {
        if(formDataSimulator.cotisationATPartPatronalePourcentage !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, cotisationATPartPatronale: formDataSimulator.cotisationAT * (- formDataSimulator.cotisationATPartPatronalePourcentage / 100) }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, cotisationATPartPatronale: 0 }));
        }
    }, [formDataSimulator.cotisationAT, formDataSimulator.cotisationATPartPatronalePourcentage, allData]);

    /*************** TAXE SUR COTISATION AT ***************/
    useEffect(() => {
        if (!allData) return;
        setFormDataSimulator((prev: any) => {
            if(prev.cotisationATPartPatronale !== 0) {
                return { ...prev, taxeSurCotisationAT: -prev.cotisationATPartPatronale };
            } else {
                return { ...prev, taxeSurCotisationAT: 0 };
            }
        });
    }, [formDataSimulator.cotisationATPartPatronale, allData]);

    // Pour le champ taxeSurCotisationATPartSalarialePourcentage 
    // JE MET RIEN PARCE QU ON SE REFERE A QUELQUE CHOSE QUI N EXISTE PAS

    // Pour le champ taxeSurCotisationATPartPatronalePourcentage
    useEffect(() => {
        if(allData?.taxe_sur_cotisation_at_partPatronale !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, taxeSurCotisationATPartPatronalePourcentage: allData?.taxe_sur_cotisation_at_partPatronale }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, taxeSurCotisationATPartPatronalePourcentage: 0 }));
        }
    }, [formDataSimulator.taxeSurCotisationAT, allData?.taxeSurCotisationATPartPatronalePourcentage, allData, allData?.taxe_sur_cotisation_at_partPatronale]);

    // Pour le champ taxeSurCotisationATPartPatronale
    useEffect(() => {
        if(formDataSimulator.taxeSurCotisationAT !== 0 && formDataSimulator.taxeSurCotisationATPartPatronalePourcentage !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, taxeSurCotisationATPartPartronale: formDataSimulator.taxeSurCotisationAT * (- formDataSimulator.taxeSurCotisationATPartPatronalePourcentage / 100) }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, taxeSurCotisationATPartPartronale: 0 }));
        }
    }, [formDataSimulator.taxeSurCotisationAT, formDataSimulator.taxeSurCotisationATPartPatronalePourcentage, allData ]);

    /*************** FONDS SOCIAL ***************/
    // Pour le champ fondsSocial
    useEffect(() => {
        setFormDataSimulator((prev: any) => ({ ...prev, fondsSocial: formDataSimulator.totalGain }));
    }, [formDataSimulator.totalGain]);

    // Pour le champ fondsSocialPartPatronalePourcentage
    useEffect(() => {
        if(allData?.FondsSocial) {
            setFormDataSimulator((prev: any) => ({ ...prev, fondsSocialPartPatronalePourcentage:  allData?.fonds_social_partPatronale}));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, fondsSocialPartPatronalePourcentage: 0 }));
        }
    }, [formDataSimulator?.fondsSocial, allData?.FondsSocial, allData?.fonds_social_partPatronale, allData]);

    // Pour le champ fondsSocialPartPatronale
    useEffect(() => {
        if(formDataSimulator.fondsSocial !== 0 && formDataSimulator.fondsSocialPartPatronalePourcentage !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, fondsSocialPartPatronale: formDataSimulator.fondsSocial * (- formDataSimulator.fondsSocialPartPatronalePourcentage / 100) }));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, fondsSocialPartPatronale: 0 }));
        }
    }, [formDataSimulator.fondsSocial, formDataSimulator.fondsSocialPartPatronalePourcentage, allData]);

    // CALCULE DES TOTAUX PART SALARIALE ET PATRONALE
    useEffect(() => { 
        setFormDataSimulator((prev: any) => ({ 
            ...prev, 
            totalPartSalariale: safeNumber(prev.carPartSalariale) + 
                               safeNumber(prev.poleEmploiPartSalariale) + 
                               safeNumber(prev.cmrcTauxPartSalariale) + 
                               safeNumber(prev.cmrcTBPartSalariale) + 
                               safeNumber(prev.ccpbCongesFormationsPartSalariale) + 
                               safeNumber(prev.ccpbPrevoyancePartSalariale) + 
                               safeNumber(prev.prevoyanceT1CCSSPartSalariale) + 
                               safeNumber(prev.prevoyanceTASSFRPartSalariale) + 
                               safeNumber(prev.prevoyanceT2CCSSPartSalariale) + 
                               safeNumber(prev.prevoyanceTBPartSalariale) + 
                               safeNumber(prev.prevoyanceTCPartSalariale) + 
                               safeNumber(prev.fraisDeSantePartSalariale || 0) + 
                               safeNumber(prev.cotisationATPartSalariale) 
        }));
        setFormDataSimulator((prev: any) => ({ 
            ...prev, 
            totalPartPatronale: safeNumber(prev.ccssPartPatronale) + 
                               safeNumber(prev.carPartPatronale) + 
                               safeNumber(prev.poleEmploiPartPatronale) + 
                               safeNumber(prev.cmrcTauxPartPatronale) + 
                               safeNumber(prev.cmrcTBPartPatronale) + 
                               safeNumber(prev.ccpbPrevoyancePartPatronale) + 
                               safeNumber(prev.ccpbCongesFormationsPartPatronale) + 
                               safeNumber(prev.prevoyanceT1CCSSPartPatronale) + 
                               safeNumber(prev.prevoyanceTASSFRPartPatronale) + 
                               safeNumber(prev.prevoyanceT2CCSSPartPatronale) + 
                               safeNumber(prev.prevoyanceTBPartPatronale) + 
                               safeNumber(prev.fraisDeSantePartPatronale || 0) + 
                               safeNumber(prev.prevoyanceTCPartPatronale) + 
                               safeNumber(prev.cotisationATPartPatronale) + 
                               safeNumber(prev.fondsSocialPartPatronale) + 
                               safeNumber(prev.taxeSurCotisationATPartPartronale)
        }));
    }, [formDataSimulator.ccssPartSalariale, formDataSimulator.carPartSalariale, formDataSimulator.poleEmploiPartSalariale, formDataSimulator.prevoyanceT1CCSSPartSalariale, formDataSimulator.cmrcTauxPartSalariale, formDataSimulator.cmrcTBPartSalariale, formDataSimulator.ccpbPrevoyancePartSalariale, formDataSimulator.ccpbCongesFormationsPartSalariale, formDataSimulator.prevoyanceT1CCSSPartSalariale, formDataSimulator.prevoyanceTASSFRPartSalariale, formDataSimulator.prevoyanceT2CCSSPartSalariale, formDataSimulator.prevoyanceTBPartSalariale, formDataSimulator.fraisDeSantePartSalariale, formDataSimulator.prevoyanceTCPartSalariale, formDataSimulator.cotisationATPartSalariale, formDataSimulator.fondsSocialPartSalariale, formDataSimulator.taxeSurCotisationATPartSalariale, formDataSimulator.ccssPartPatronale, formDataSimulator.carPartPatronale, formDataSimulator.poleEmploiPartPatronale, formDataSimulator.prevoyanceT1CCSSPartPatronale, formDataSimulator.cmrcTauxPartPatronale, formDataSimulator.cmrcTBPartPatronale, formDataSimulator.ccpbPrevoyancePartPatronale, formDataSimulator.ccpbCongesFormationsPartPatronale, formDataSimulator.prevoyanceT1CCSSPartPatronale, formDataSimulator.prevoyanceTASSFRPartPatronale, formDataSimulator.prevoyanceT2CCSSPartPatronale, formDataSimulator.prevoyanceTBPartPatronale, formDataSimulator.fraisDeSantePartPatronale, formDataSimulator.prevoyanceTCPartPatronale, formDataSimulator.cotisationATPartPatronale, formDataSimulator.fondsSocialPartPatronale, formDataSimulator.taxeSurCotisationATPartPatronale]);
    
    /*************** TITRE RESTAURANT ***************/
    // Pour le champ montantSalarialTitresRestaurantAuto
    useEffect(() => {
        if(formDataSimulator.nbTitresRestaurant !== 0 && formDataSimulator.montantSalarialTitresRestaurant !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, montantSalarialAutoTitresRestaurant: formDataSimulator.nbTitresRestaurant * (- formDataSimulator.montantSalarialTitresRestaurant)}));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, montantSalarialAutoTitresRestaurant: 0 }));
        }
    }, [formDataSimulator.nbTitresRestaurant, formDataSimulator.montantSalarialTitresRestaurant]);

    // Pour le champ montantPatronalTitresRestaurantAuto
    useEffect(() => {
        if(formDataSimulator.nbTitresRestaurant !== 0 && formDataSimulator.montantPatronalTitresRestaurant !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, montantPatronalAutoTitresRestaurant: formDataSimulator.nbTitresRestaurant * (- formDataSimulator.montantPatronalTitresRestaurant)}));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, montantPatronalAutoTitresRestaurant: 0 }));
        }
    }, [formDataSimulator.nbTitresRestaurant, formDataSimulator.montantPatronalTitresRestaurant]);

    /*************** PANIERS ***************/
    // Pour le champ montantSalarialAutoPaniers
    useEffect(() => {
        if(formDataSimulator.nbPaniers !== 0 && formDataSimulator.montantSalarialPaniers !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, montantSalarialAutoPaniers: formDataSimulator.nbPaniers * formDataSimulator.montantSalarialPaniers}));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, montantSalarialAutoPaniers: 0 }));
        }
    }, [formDataSimulator.nbPaniers, formDataSimulator.montantSalarialPaniers]);

    /*************** 5% MONEGASQUE ***************/
    useEffect(() => {
        if(formDataSimulator.salaireTaux !== 0) {
            const calcul = (getSalaireGainBrut(formDataSimulator) + formDataSimulator.salaireGainHeuresSup125 + formDataSimulator.salaireGainHeuresSup150 + formDataSimulator.primeAncienneteMontant) * 0.05;
            if(allData?.indemnite5) {
                setFormDataSimulator((prev: any) => ({ ...prev, montant5Monegasque: calcul }));
            } else {
                if(allData?.salaireTaux <= allData?.smic_amount + 0.01) {
                    setFormDataSimulator((prev: any) => ({ ...prev, montant5Monegasque: calcul }));
                } else {
                    setFormDataSimulator((prev: any) => ({ ...prev, montant5Monegasque: 0 }));
                }
            }
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, montant5Monegasque: 0 }));
        }
    }, [formDataSimulator.tauxHeuresSup125, formDataSimulator.salaireTaux, formDataSimulator.salaireGain, formDataSimulator.salaireGainBrut, formDataSimulator.inputMode, formDataSimulator.salaireGainHeuresSup125, formDataSimulator.salaireGainHeuresSup150, formDataSimulator.primeAncienneteMontant, allData?.indemnite5, allData?.salaireTaux, allData?.smic_amount]);


    /*************** AVANTAGE EN NATURE ***************/
    useEffect(() => {
        if(formDataSimulator.avantageEnNature !== 0) {
            setFormDataSimulator((prev: any) => ({ ...prev, avantageEnNaturePatronale: - prev.avantageEnNature}));
        } else {
            setFormDataSimulator((prev: any) => ({ ...prev, avantageEnNaturePatronale: 0 }));
        }
    }, [formDataSimulator.avantageEnNature]);

    /*************** TOTAL NET ***************/
    useEffect(() => {
        setFormDataSimulator((prev: any) => {
            const totalNet = parseFloat(
                (
                    safeNumber(prev.totalGain) +
                    safeNumber(prev.totalPartSalariale) +
                    safeNumber(prev.montantSalarialAutoTitresRestaurant) +
                    safeNumber(prev.montantSalarialAutoPaniers) +
                    safeNumber(prev.montant5Monegasque) +
                    safeNumber(prev.avantageEnNaturePatronale) +
                    safeNumber(prev.remboursementTransportPatronale) +
                    safeNumber(prev.indemniteTeletravail) +
                    safeNumber(prev.fraisDiversesPatronale)
                ).toFixed(2)
            );
            if (prev.totalNet === totalNet) return prev;
            return { ...prev, totalNet };
        });
    }, [formDataSimulator.totalGain, formDataSimulator.remboursementTransportPatronale, formDataSimulator.totalPartSalariale, formDataSimulator.salaireTaux, formDataSimulator.montantSalarialAutoTitresRestaurant, formDataSimulator.montantSalarialAutoPaniers, formDataSimulator.montant5Monegasque, formDataSimulator.avantageEnNaturePatronale, formDataSimulator.indemniteTeletravail, formDataSimulator.fraisDiversesPatronale]);

    // Mode NET : ajustement fin du brut si totalNet réel diffère du net saisi (arrondis en cascade)
    useEffect(() => {
        if (!allData || inputMode !== 'NET') {
            netRefineAttemptsRef.current = 0;
            return;
        }

        const targetNet = safeNumber(formDataSimulator.salaireGain);
        if (
            formDataSimulator.salaireBaseHeures === "" ||
            formDataSimulator.salaireGain === "" ||
            !targetNet
        ) {
            netRefineAttemptsRef.current = 0;
            return;
        }

        const currentNet = safeNumber(formDataSimulator.totalNet);
        const diff = parseFloat((targetNet - currentNet).toFixed(2));

        if (Math.abs(diff) < 0.005) {
            netRefineAttemptsRef.current = 0;
            return;
        }

        if (Math.abs(diff) > 0.05 || netRefineAttemptsRef.current >= 8) {
            return;
        }

        const currentBrut = safeNumber(formDataSimulator.salaireGainBrut);
        const newBrut = parseFloat((currentBrut + (diff > 0 ? 0.01 : -0.01)).toFixed(2));
        if (newBrut <= 0 || newBrut === currentBrut) return;

        netRefineAttemptsRef.current += 1;
        setFormDataSimulator((prev: any) => ({ ...prev, salaireGainBrut: newBrut }));
    }, [
        inputMode,
        allData,
        formDataSimulator.totalNet,
        formDataSimulator.salaireGain,
        formDataSimulator.salaireGainBrut,
        formDataSimulator.salaireBaseHeures,
        setFormDataSimulator,
    ]);

    /*************** TOTAL COUT BRUT***************/
    useEffect(() => {
        setFormDataSimulator((prev: any) => ({ ...prev, 
            totalCoutBrut: safeNumber(prev.totalGain) -
                     safeNumber(prev.totalPartPatronale) +
                     Math.abs(safeNumber(prev.montantPatronalAutoTitresRestaurant)) +
                     safeNumber(prev.montantSalarialAutoPaniers) +
                     safeNumber(prev.montant5Monegasque) +
                     safeNumber(prev.avantageEnNaturePatronale) +
                     safeNumber(prev.remboursementTransportPatronale) +
                     safeNumber(prev.indemniteTeletravail) +
                     safeNumber(prev.fraisDiversesPatronale)
        
        }));

    }, [formDataSimulator.salaireBaseHeures ,formDataSimulator.heuresSup125, formDataSimulator.heuresSup150, formDataSimulator.totalGain, formDataSimulator.totalPartPatronale, formDataSimulator.salaireTaux, formDataSimulator.montantPatronalAutoTitresRestaurant, formDataSimulator.montantSalarialAutoPaniers, formDataSimulator.montant5Monegasque, formDataSimulator.avantageEnNaturePatronale, formDataSimulator.remboursementTransportPatronale, formDataSimulator.indemniteTeletravail, formDataSimulator.fraisDiversesPatronale, allData]);

    // Ne pas rendre le contenu tant que les données ne sont pas chargées
    if (isLoading || !allData) {
        return (
            <div className="custom-scroll mt-6 flex flex-1 flex-col gap-5 overflow-y-auto pr-1 min-h-0 w-10/12 mx-auto bg-white/95 h-full mb-10 pb-10 items-center justify-center">
                <p className="text-grey">Chargement des paramètres...</p>
            </div>
        );
    }

    const annuelBrut = parseFloat((safeNumber(formDataSimulator.totalGain) * 12).toFixed(2));
    const annuelNet = parseFloat((safeNumber(formDataSimulator.totalNet) * 12).toFixed(2));
    const annuelEmployeur = parseFloat((safeNumber(formDataSimulator.totalCoutBrut) * 12).toFixed(2));

    return (
    <div className="custom-scroll mt-6 flex flex-1 flex-col gap-5 overflow-y-auto pr-1 min-h-0 w-10/12 mx-auto bg-white/95 h-full mb-10 pb-10">
        <div className='w-full bg-blue-500/20 p-5 rounded-xl'>
          <p className='text-sm text-grey font-bold text-center'>{annual ? "SIMULATION ANNUELLE" : "SIMULATION MENSUELLE"}</p>
        </div>

        <section className="w-full border border-gray-100 rounded-xl shadow-md backdrop-blur-sm p-5">
            <p className="text-sm text-grey font-bold mb-3">TYPE DE SAISIE DES GAINS</p>
            <div className="flex gap-3">
                <button
                    type="button"
                    onClick={() => handleInputModeChange('BRUT')}
                    className={`px-5 py-2 rounded-lg text-sm font-bold transition-colors ${
                        inputMode === 'BRUT'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-grey hover:bg-gray-200'
                    }`}
                >
                    BRUT
                </button>
                <button
                    type="button"
                    onClick={() => handleInputModeChange('NET')}
                    className={`px-5 py-2 rounded-lg text-sm font-bold transition-colors ${
                        inputMode === 'NET'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-grey hover:bg-gray-200'
                    }`}
                >
                    NET
                </button>
            </div>
            <p className="text-xs text-grey italic mt-2">
                {isNetMode
                    ? "Saisissez le salaire en net : le simulateur calcule le brut correspondant."
                    : "Saisissez le salaire en brut : le simulateur calcule le net correspondant."}
            </p>
        </section>

        <section className={`w-full border border-b border-gray-100 rounded-xl shadow-md backdrop-blur-sm p-5`}>
            <div className='grid grid-cols-5 gap-4 w-full items-center'>
                <div className="col-span-2"></div>
                <p className="text-sm text-grey font-bold">HEURES</p>
                <p className="text-sm text-grey font-bold">TAUX SAL.</p>
                <p className="text-sm text-grey font-bold">GAIN {isNetMode ? '(NET)' : '(BRUT)'}</p>
            </div>
            <div className="grid grid-cols-5 gap-4 w-full items-center">
                <p className="text-sm text-grey col-span-2">SALAIRE DE BASE <br/> <span className="text-xs text-grey italic">(169h pour un temps plein)</span></p>
                <Input
                    label=""
                    name="salaireBaseHeures"
                    type="number"
                    value={formDataSimulator.salaireBaseHeures || ""}
                    onChange={handleChange}
                    suffix="h"
                    clasName="col-span-1"
                />
                <Input
                    label=""
                    name="salaireTaux"
                    type="number"
                    disabled={true}
                    suffix="€"
                    value={formatEuroValue(formDataSimulator.salaireTaux)}
                    onChange={handleChange}
                    clasName="col-span-1"
                />
                <Input
                    label=""
                    name="salaireGain"
                    type="number"
                    suffix="€"
                    value={formatEuroValue(formDataSimulator.salaireGain)}
                    onChange={handleChange}
                    clasName="col-span-1"
                />
            </div>

            <div className="grid grid-cols-5 gap-4 w-full items-center">
                <p className="text-sm text-grey col-span-2">HEURES SUP 125%</p>
                <Input
                    label=""
                    name="heuresSup125"
                    type="number"
                    suffix="h"
                    disabled={inputMode === 'NET'}
                    value={formDataSimulator.heuresSup125 || ""}
                    onChange={handleChange}
                    clasName="col-span-1"
                />
                <Input
                    label=""
                    name="tauxHeuresSup125"
                    type="number"
                    disabled={true}
                    suffix="€"
                    value={formatEuroValue(formDataSimulator.tauxHeuresSup125)}
                    onChange={handleChange}
                    clasName="col-span-1"
                />
                <Input
                    label=""
                    name="salaireGainHeuresSup125"
                    type="number"
                    disabled={true}
                    suffix="€"
                    value={formatEuroValue(formDataSimulator.salaireGainHeuresSup125)}
                    onChange={handleChange}
                    clasName="col-span-1"
                />
            </div>

            <div className="grid grid-cols-5 gap-4 w-full items-center">
                <p className="text-sm text-grey col-span-2">HEURES SUP 150%</p>
                <Input
                    label=""
                    name="heuresSup150"
                    type="number"
                    value={formDataSimulator.heuresSup150 || ""}
                    onChange={handleChange}
                    disabled={inputMode === 'NET'}
                    suffix="h"
                    clasName="col-span-1"
                />
                <Input
                    label=""
                    name="tauxHeuresSup150"
                    type="number"
                    disabled={true}
                    value={formatEuroValue(formDataSimulator.tauxHeuresSup150)}
                    onChange={handleChange}
                    suffix="€"
                    clasName="col-span-1"
                />
                <Input
                    label=""
                    name="salaireGainHeuresSup150"
                    type="number"
                    disabled={true}
                    suffix="€"
                    value={formatEuroValue(formDataSimulator.salaireGainHeuresSup150)}
                    onChange={handleChange}
                    clasName="col-span-1"
                />
            </div>

            <div className="grid grid-cols-5 gap-4 w-full items-center">
                <p className="text-sm text-grey col-span-3">PRIME D'ANC. CALC. SUR SALAIRE DE BASE UNIQUEMENT</p>
                <Input
                    label=""
                    name="primeAnciennetePourcentage"
                    type="number"
                    value={formDataSimulator.primeAnciennetePourcentage || ""}
                    onChange={handleChange}
                    disabled={inputMode === 'NET'}
                    suffix="%"
                    clasName="col-span-1"
                />
                <Input
                    label=""
                    name="primeAncienneteMontant"
                    type="number"
                    value={formatEuroValue(formDataSimulator.primeAncienneteMontant)}
                    onChange={handleChange}
                    disabled={true}
                    suffix="€"
                    clasName="col-span-1"
                />
            </div>

            <div className="grid grid-cols-5 gap-4 w-full items-center">
                <p className="text-sm text-grey col-span-4">BONUS/PRIME</p>
                <Input
                    label=""
                    name="bonusPrime"
                    type="number"
                    value={formatEuroValue(formDataSimulator.bonusPrime)}
                    onChange={handleChange}
                    disabled={inputMode === 'NET'}
                    suffix="€"
                    clasName="col-span-1"
                />
            </div>
            <div className="grid grid-cols-5 gap-4 w-full items-center">
                <p className="text-sm text-grey col-span-4">AVANTAGE EN NATURE</p>
                <Input
                    label=""
                    name="avantageEnNature"
                    type="number"
                    disabled={inputMode === 'NET'}
                    value={formatEuroValue(formDataSimulator.avantageEnNature)}
                    onChange={handleChange}
                    suffix="€"
                    clasName="col-span-1"
                />
            </div>

            <div className="grid grid-cols-5 gap-4 w-full items-center bg-blue-500/20 p-2 mt-4 rounded-xl">
                <p className="text-md text-grey font-bold col-span-2">{isNetMode ? 'TOTAL NET' : 'TOTAL BRUT'}</p>
                <p className='text-md text-grey font-bold col-span-1'>{formDataSimulator.totalHeures} heures</p>
                <div className='col-span-1'></div>
                <p className="text-md text-grey col-span-1 font-bold">
                    {formatComptableMaisPasTrop(
                        (isNetMode
                            ? formDataSimulator.totalNet
                            : formDataSimulator.totalGain
                        )?.toFixed(2) || "0.00"
                    )} €
                </p>
            </div>
            {isNetMode && safeNumber(formDataSimulator.salaireGainBrut) > 0 && (
                <div className="grid grid-cols-5 gap-4 w-full items-center bg-blue-500/10 p-2 mt-2 rounded-xl">
                    <p className="text-sm text-grey font-bold col-span-4 text-end">Salaire de base brut calculé</p>
                    <p className="text-sm text-grey col-span-1 font-bold">{formatComptableMaisPasTrop(formDataSimulator.salaireGainBrut?.toFixed(2) || "0.00")} €</p>
                </div>
            )}
        </section>

        <section className={`w-full border border-b border-gray-100 rounded-xl shadow-md backdrop-blur-sm p-5 pb-8 mb-4`}>
            <div className='grid grid-cols-5 gap-4 w-full items-center'>
                <div className='col-span-3'></div>
                <p className="text-sm text-grey font-bold col-span-1">PART SALARIALE</p>
                <p className="text-sm text-grey font-bold col-span-1">PART PATRONALE</p>
            </div>

            <div className='grid grid-cols-5 gap-4 w-full items-center'>
                <p className="text-sm text-grey col-span-2">CCSS</p>
                <Input
                    label=""
                    name="ccssEuro"
                    type="number"
                    disabled={true}
                    value={formatEuroValue(formDataSimulator.ccssEuro)}
                    onChange={handleChange}
                    clasName="col-span-1"
                    suffix="€"
                />
                <div className='flex items-center gap-2 w-full col-span-1'>

                </div>
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <p className='text-xs text-grey font-thin w-1/3' id="ccssPartPatronalePourcentage">{formDataSimulator.ccssPartPatronalePourcentage}%</p>
                    <Input
                        label=""
                        name="ccssPartPatronale"
                        type="number"
                        disabled={true}
                        value={formatEuroValue(formDataSimulator.ccssPartPatronale)}
                        onChange={handleChange}
                        clasName="w-2/3"
                        suffix="€"
                        />
                </div>
            </div>

            <div className='grid grid-cols-5 gap-4 w-full items-center'>
                <p className="text-sm text-grey col-span-2">CAR</p>
                <Input
                    label=""
                    name="car"
                    type="number"
                    disabled={true}
                    value={formatEuroValue(formDataSimulator.car)}
                    onChange={handleChange}
                    clasName="col-span-1"
                    suffix="€"
                />
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <p className='text-xs text-grey font-thin w-1/3'>{formDataSimulator.carPartSalarialePourcentage || 0} %</p>
                    <Input
                        label=""
                        name="carPartSalariale"
                        type="number"
                        disabled={true}
                        value={formatEuroValue(formDataSimulator.carPartSalariale)}
                        onChange={handleChange}
                        clasName="w-2/3"
                        suffix="€"
                        />
                </div>
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <p className='text-xs text-grey font-thin w-1/3'>{formDataSimulator.carPartPatronalePourcentage || 0} %</p>
                    <Input
                        label=""
                        name="carPartPatronale"
                        type="number"
                        disabled={true}
                        value={formatEuroValue(formDataSimulator.carPartPatronale)}
                        onChange={handleChange}
                        clasName="w-2/3"
                        suffix="€"
                        />
                </div>
            </div>

            <div className='grid grid-cols-5 gap-4 w-full items-center'>
                <p className="text-sm text-grey col-span-2">POLE EMPLOI</p>
                <Input
                    label=""
                    name="poleEmploi"
                    type="number"
                    disabled={true}
                    value={formatEuroValue(formDataSimulator.poleEmploi)}
                    onChange={handleChange}
                    clasName="col-span-1"
                    suffix="€"
                />
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <p className='text-xs text-grey font-thin w-1/3'>{formDataSimulator.poleEmploiPartSalarialePourcentage} %</p>
                    <Input
                        label=""
                        name="poleEmploiPartSalariale"
                        type="number"
                        disabled={true}
                        value={formatEuroValue(formDataSimulator.poleEmploiPartSalariale)}
                        onChange={handleChange}
                        clasName="w-2/3"
                        suffix="€"
                        />
                </div>
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <p className='text-xs text-grey font-thin w-1/3'>{formDataSimulator.poleEmploiPartPatronalePourcentage} %</p>
                    <Input
                        label=""
                        name="poleEmploiPartPatronale"
                        type="number"
                        disabled={true}
                        value={formatEuroValue(formDataSimulator.poleEmploiPartPatronale)}
                        onChange={handleChange}
                        clasName="w-2/3"
                        suffix="€"
                        />
                </div>
            </div>

            <div className='grid grid-cols-5 gap-4 w-full items-center'>
                <p className="text-sm text-grey col-span-2">CMRC TA</p>
                <Input
                    label=""
                    name="cmrcTaux"
                    type="number"
                    disabled={true}
                    value={formatEuroValue(formDataSimulator.cmrcTaux)}
                    onChange={handleChange}
                    clasName="col-span-1"
                    suffix="€"
                />
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <p className='text-xs text-grey font-thin w-1/3'>{formDataSimulator.cmrcTauxPartSalarialePourcentage || 0} %</p>
                    <Input
                        label=""
                        name="cmrcTauxPartSalariale"
                        type="number"
                        disabled={true}
                        value={formatEuroValue(formDataSimulator.cmrcTauxPartSalariale)}
                        onChange={handleChange}
                        clasName="w-2/3"
                        suffix="€"
                        />
                </div>
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <p className='text-xs text-grey font-thin w-1/3'>{formDataSimulator.cmrcTauxPartPatronalePourcentage || 0} %</p>
                    <Input
                        label=""
                        name="cmrcTauxPartPatronale"
                        type="number"
                        disabled={true}
                        value={formatEuroValue(formDataSimulator.cmrcTauxPartPatronale)}
                        onChange={handleChange}
                        clasName="w-2/3"
                        suffix="€"
                        />
                </div>
            </div>

            <div className='grid grid-cols-5 gap-4 w-full items-center'>
                <p className="text-sm text-grey col-span-2">CMRC TB</p>
                <Input
                    label=""
                    name="cmrcTB"
                    type="number"
                    disabled={true}
                    value={formatEuroValue(formDataSimulator.cmrcTB)}
                    onChange={handleChange}
                    clasName="col-span-1"
                    suffix="€"
                />
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <p className='text-xs text-grey font-thin w-1/3'>{formDataSimulator.cmrcTBPartSalarialePourcentage || 0} %</p>
                    <Input
                        label=""
                        name="cmrcTBPartSalariale"
                        type="number"
                        disabled={true}
                        value={formatEuroValue(formDataSimulator.cmrcTBPartSalariale)}
                        onChange={handleChange}
                        clasName="w-2/3"
                        suffix="€"
                        />
                </div>
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <p className='text-xs text-grey font-thin w-1/3'>{formDataSimulator.cmrcTBPartPatronalePourcentage || 0} %</p>
                    <Input
                        label=""
                        name="cmrcTBPartPatronale"
                        type="number"
                        disabled={true}
                        value={formatEuroValue(formDataSimulator.cmrcTBPartPatronale)}
                        onChange={handleChange}
                        clasName="w-2/3"
                        suffix="€"
                        />
                </div>
            </div>

            <div className='grid grid-cols-5 gap-4 w-full items-center'>
                <p className="text-sm text-grey col-span-2">CCPB CONGES + FORMATIONS</p>
                <Input
                    label=""
                    name="ccpbCongesFormations"
                    type="number"
                    disabled={true}
                    value={formatEuroValue(formDataSimulator.ccpbCongesFormations)}
                    onChange={handleChange}
                    clasName="col-span-1"
                    suffix="€"
                />
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <p className='text-xs text-grey font-thin w-1/3'>{formDataSimulator.ccpbCongesFormationsPartSalarialePourcentage || 0} %</p>
                    <Input
                        label=""
                        name="ccpbCongesFormationsPartSalariale"
                        type="number"
                        disabled={true}
                        value={formatEuroValue(formDataSimulator.ccpbCongesFormationsPartSalariale)}
                        onChange={handleChange}
                        clasName="w-2/3"
                        suffix="€"
                        />
                </div>
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <p className='text-xs text-grey font-thin w-1/3'>{formDataSimulator?.ccpbCongesFormationsPartPatronalePourcentage || 0} %</p>
                    <Input
                        label=""
                        name="ccpbCongesFormationsPartPatronale"
                        type="number"
                        disabled={true}
                        value={formatEuroValue(formDataSimulator.ccpbCongesFormationsPartPatronale)}
                        onChange={handleChange}
                        clasName="w-2/3"
                        suffix="€"
                        />
                </div>
            </div>

            <div className='grid grid-cols-5 gap-4 w-full items-center'>
                <p className="text-sm text-grey col-span-2">CCPB PREVOYANCE</p>
                <Input
                    label=""
                    name="ccpbPrevoyance"
                    type="number"
                    disabled={true}
                    value={formatEuroValue(formDataSimulator.ccpbPrevoyance)}
                    onChange={handleChange}
                    clasName="col-span-1"
                    suffix="€"
                />
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <p className='text-xs text-grey font-thin w-1/3'>{formDataSimulator.ccpbPrevoyancePartSalarialePourcentage} %</p>
                    <Input
                        label=""
                        name="ccpbPrevoyancePartSalariale"
                        type="number"
                        disabled={true}
                        value={formatEuroValue(formDataSimulator.ccpbPrevoyancePartSalariale)}
                        onChange={handleChange}
                        clasName="w-2/3"
                        suffix="€"
                        />
                </div>
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <p className='text-xs text-grey font-thin w-1/3'>{formDataSimulator.ccpbPrevoyancePartPatronalePourcentage} %</p>
                    <Input
                        label=""
                        name="ccpbPrevoyancePartPatronale"
                        type="number"
                        disabled={true}
                        value={formatEuroValue(formDataSimulator.ccpbPrevoyancePartPatronale)}
                        onChange={handleChange}
                        clasName="w-2/3"
                        suffix="€"
                        />
                </div>
            </div>

            <div className='grid grid-cols-5 gap-4 w-full items-center'>
                <p className="text-sm text-grey col-span-2">PRÉVOYANCE T1 CCSS</p>
                <Input
                    label=""
                    name="prevoyanceT1CCSS"
                    type="number"
                    disabled={true}
                    value={formatEuroValue(formDataSimulator.prevoyanceT1CCSS)}
                    onChange={handleChange} 
                    clasName="col-span-1"
                    suffix="€"
                />
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <p className='text-xs text-grey font-thin w-1/3'>{formDataSimulator.prevoyanceT1CCSSPartSalarialePourcentage} %</p>
                    <Input
                        label=""
                        name="prevoyanceT1CCSSPartSalariale"
                        type="number"
                        disabled={true}
                        value={formatEuroValue(formDataSimulator.prevoyanceT1CCSSPartSalariale)}
                        onChange={handleChange}
                        clasName="w-2/3"
                        suffix="€"
                        />
                </div>
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <p className='text-xs text-grey font-thin w-1/3'>{formDataSimulator.prevoyanceT1CCSSPartPatronalePourcentage} %</p>
                    <Input
                        label=""
                        name="prevoyanceT1CCSSPartPatronale"
                        type="number"
                        disabled={true}
                        value={formatEuroValue(formDataSimulator.prevoyanceT1CCSSPartPatronale)}
                        onChange={handleChange}
                        clasName="w-2/3"
                        suffix="€"
                        />
                </div>
            </div>

            <div className='grid grid-cols-5 gap-4 w-full items-center'>
                <p className="text-sm text-grey col-span-2">PRÉVOYANCE TA SS FR</p>
                <Input
                    label=""
                    name="prevoyanceTASSFR"
                    type="number"
                    disabled={true}
                    value={formatEuroValue(formDataSimulator.prevoyanceTASSFR)}
                    onChange={handleChange} 
                    clasName="col-span-1"
                    suffix="€"
                />
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <p className='text-xs text-grey font-thin w-1/3'>{formDataSimulator.prevoyanceTASSFRPartSalarialePourcentage} %</p>
                    <Input
                        label=""
                        name="prevoyanceTASSFRPartSalariale"
                        type="number"
                        disabled={true}
                        value={formatEuroValue(formDataSimulator.prevoyanceTASSFRPartSalariale)}
                        onChange={handleChange}
                        clasName="w-2/3"
                        suffix="€"
                        />
                </div>
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <p className='text-xs text-grey font-thin w-1/3'>{formDataSimulator.prevoyanceTASSFRPartPatronalePourcentage} %</p>
                    <Input
                        label=""
                        name="prevoyanceTASSFRPartPatronale"
                        type="number"
                        disabled={true}
                        value={formatEuroValue(formDataSimulator.prevoyanceTASSFRPartPatronale)}
                        onChange={handleChange}
                        clasName="w-2/3"
                        suffix="€"
                        />
                </div>
            </div>

            <div className='grid grid-cols-5 gap-4 w-full items-center'>
                <p className="text-sm text-grey col-span-2">PRÉVOYANCE T2 CCSS</p>
                <Input
                    label=""
                    name="prevoyanceT2CCSS"
                    type="number"
                    disabled={true}
                    value={formatEuroValue(formDataSimulator.prevoyanceT2CCSS)}
                    onChange={handleChange} 
                    clasName="col-span-1"
                    suffix="€"
                />
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <p className='text-xs text-grey font-thin w-1/3'>{formDataSimulator.prevoyanceT2CCSSPartSalarialePourcentage} %</p>
                    <Input
                        label=""
                        name="prevoyanceT2CCSSPartSalariale"
                        type="number"
                        disabled={true}
                        value={formatEuroValue(formDataSimulator.prevoyanceT2CCSSPartSalariale)}
                        onChange={handleChange}
                        clasName="w-2/3"    
                        suffix="€"
                        />
                </div>
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <p className='text-xs text-grey font-thin w-1/3'>{formDataSimulator.prevoyanceT2CCSSPartPatronalePourcentage} % </p>
                    <Input
                        label=""
                        name="prevoyanceT2CCSSPartPatronale"
                        type="number"
                        disabled={true}
                        value={formatEuroValue(formDataSimulator.prevoyanceT2CCSSPartPatronale)}
                        onChange={handleChange}
                        clasName="w-2/3"
                        suffix="€"
                        />
                </div>
            </div>

            <div className='grid grid-cols-5 gap-4 w-full items-center'>
                <p className="text-sm text-grey col-span-2">PRÉVOYANCE TB </p>
                <Input
                    label=""
                    name="prevoyanceTB"
                    type="number"
                    disabled={true}
                    value={formatEuroValue(formDataSimulator.prevoyanceTB)}
                    onChange={handleChange} 
                    clasName="col-span-1"
                    suffix="€"
                />
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <p className='text-xs text-grey font-thin w-1/3'>{formDataSimulator.prevoyanceTBPartSalarialePourcentage} %</p>
                    <Input
                        label=""
                        name="prevoyanceTBPartSalariale"
                        type="number"
                        disabled={true}
                        value={formatEuroValue(formDataSimulator.prevoyanceTBPartSalariale)}
                        onChange={handleChange}
                        clasName="w-2/3"
                        suffix="€"
                        />
                </div>
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <p className='text-xs text-grey font-thin w-1/3'>{formDataSimulator.prevoyanceTBPartPatronalePourcentage} %</p>
                    <Input
                        label=""
                        name="prevoyanceTBPartPatronale"
                        type="number"
                        disabled={true}
                        value={formatEuroValue(formDataSimulator.prevoyanceTBPartPatronale)}
                        onChange={handleChange}
                        clasName="w-2/3"
                        suffix="€"
                        />
                </div>
            </div>

            <div className='grid grid-cols-5 gap-4 w-full items-center'>
                <p className="text-sm text-grey col-span-2">PRÉVOYANCE TC </p>
                <Input
                    label=""
                    name="prevoyanceTC"
                    type="number"
                    disabled={true}
                    value={formatEuroValue(formDataSimulator.prevoyanceTC)}
                    onChange={handleChange} 
                    clasName="col-span-1"
                    suffix="€"
                />
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <p className='text-xs text-grey font-thin w-1/3'>{formDataSimulator.prevoyanceTCPartSalarialePourcentage} %</p>
                    <Input
                        label=""
                        name="prevoyanceTCPartSalariale"
                        type="number"
                        disabled={true}
                        value={formatEuroValue(formDataSimulator.prevoyanceTCPartSalariale)}
                        onChange={handleChange}
                        clasName="w-2/3"
                        suffix="€"
                        />
                </div>
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <p className='text-xs text-grey font-thin w-1/3'>{formDataSimulator.prevoyanceTCPartPatronalePourcentage} %</p>
                    <Input
                        label=""
                        name="prevoyanceTCPartPatronale"
                        type="number"
                        disabled={true}
                        value={formatEuroValue(formDataSimulator.prevoyanceTCPartPatronale)}
                        onChange={handleChange}
                        clasName="w-2/3"
                        suffix="€"
                        />
                </div>
            </div>

            <div className='grid grid-cols-5 gap-4 w-full items-center'>
                <p className="text-sm text-grey col-span-2">FRAIS DE SANTÉ </p>
                <div className='col-span-1'></div>
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <p className='text-xs text-grey font-thin w-1/3'></p>
                    <Input
                        label=""
                        name="fraisDeSantePartSalariale"
                        type="number"
                        value={formatEuroValue(formDataSimulator.fraisDeSantePartSalariale)}
                        onChange={handleChange}
                        clasName="w-2/3"
                        suffix="€"
                        />
                </div>
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <p className='text-xs text-grey font-thin w-1/3'></p>
                    <Input
                        label=""
                        name="fraisDeSantePartPatronale"
                        type="number"
                        value={formatEuroValue(formDataSimulator.fraisDeSantePartPatronale)}
                        onChange={handleChange}
                        clasName="w-2/3"
                        suffix="€"
                        />
                </div>
            </div>

            <div className='grid grid-cols-5 gap-4 w-full items-center'>
                <p className="text-sm text-grey col-span-2">COTISATION AT </p>
                <Input
                    label=""
                    name="cotisationAT"
                    type="number"
                    disabled={true}
                    value={formatEuroValue(formDataSimulator.cotisationAT)}
                    onChange={handleChange} 
                    clasName="col-span-1"
                />
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <p className='text-xs text-grey font-thin w-1/3'>{formDataSimulator.cotisationATPartSalarialePourcentage} %</p>
                    <Input
                        label=""
                        name="cotisationATPartSalariale"
                        type="number"
                        disabled={true}
                        value={formatEuroValue(formDataSimulator.cotisationATPartSalariale)}
                        onChange={handleChange}
                        clasName="w-2/3"
                        suffix="€"
                        />
                </div>
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <p className='text-xs text-grey font-thin w-1/3'>{formatEuroValue(formDataSimulator.cotisationATPartPatronalePourcentage)} %</p>
                    <Input
                        label=""
                        name="cotisationATPartPatronale"
                        type="number"
                        disabled={true}
                        value={formatEuroValue(formDataSimulator.cotisationATPartPatronale)}
                        onChange={handleChange}
                        clasName="w-2/3"
                        suffix="€"
                        />
                </div>
            </div>

            <div className='grid grid-cols-5 gap-4 w-full items-center'>
                <p className="text-sm text-grey col-span-2">TAXE SUR COTISATION AT </p>
                <Input
                    label=""
                    name="taxeSurCotisationAT"
                    type="number"
                    disabled={true}
                    value={formatEuroValue(formDataSimulator.taxeSurCotisationAT)}
                    onChange={handleChange} 
                    clasName="col-span-1"
                />
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <p className='text-xs text-grey font-thin w-1/3'></p>

                </div>
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <p className='text-xs text-grey font-thin w-1/3'>{formatEuroValue(formDataSimulator.taxeSurCotisationATPartPatronalePourcentage)} %</p>
                    <Input
                        label=""
                        name="taxeSurCotisationATPartPartronale"
                        type="number"
                        disabled={true}
                        value={formatEuroValue(formDataSimulator.taxeSurCotisationATPartPartronale)}
                        onChange={handleChange}
                        clasName="w-2/3"
                        suffix="€"
                        />
                </div>
            </div>

            <div className='grid grid-cols-5 gap-4 w-full items-center'>
                <p className="text-sm text-grey col-span-2">FONDS SOCIAL </p>
                <Input
                    label=""
                    name="fondsSocial"
                    type="number"
                    disabled={true}
                    value={formDataSimulator.fondsSocial || ""}
                    onChange={handleChange} 
                    clasName="col-span-1"
                    suffix="€"
                />
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <p className='text-xs text-grey font-thin w-1/3'></p>
                </div>
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <p className='text-xs text-grey font-thin w-1/3'>{formDataSimulator.fondsSocialPartPatronalePourcentage} %</p>
                    <Input
                        label=""
                        name="fondsSocialPartPatronale"
                        type="number"
                        disabled={true}
                        value={formatEuroValue(formDataSimulator.fondsSocialPartPatronale)}
                        onChange={handleChange}
                        clasName="w-2/3"
                        suffix="€"
                        />
                </div>
            </div>

            <div className="grid grid-cols-5 gap-4 w-full items-center bg-blue-500/20 p-2 mt-4 rounded-xl">
                <p className="text-md text-grey font-bold col-span-2">TOTAL </p>
                <div className='col-span-1'></div>
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <p className='text-xs text-grey font-thin w-1/3'></p>
                    <p className='text-md font-bold text-grey w-2/3'>{formDataSimulator.totalPartSalariale ? formatComptableMaisPasTrop(formDataSimulator.totalPartSalariale.toFixed(2)) : "0.00"} €</p>
                </div>
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <p className='text-xs text-grey font-thin w-1/3'></p>
                    <p className='text-md font-bold text-grey w-2/3'>{formDataSimulator.totalPartPatronale ? formatComptableMaisPasTrop(formDataSimulator.totalPartPatronale.toFixed(2)) : "0.00"} €</p>
                </div>
            </div>

        </section>

        <section className={`w-full border border-b border-gray-100 rounded-xl shadow-md backdrop-blur-sm p-5 pb-8 mb-4`}>
            <div className='grid grid-cols-5 gap-4 w-full items-center'>
                <p className="text-sm text-grey font-bold col-span-2">Non-soumis</p>
                <p className="text-sm text-grey font-bold col-span-1">Nombre</p>
                <p className="text-sm text-grey font-bold col-span-1">Montant salarial</p>
                <p className="text-sm text-grey font-bold col-span-1">Montant patronal</p>
            </div>
            <div className='grid grid-cols-5 gap-4 w-full items-center'>
                <p className="text-sm text-grey col-span-2">TITRES RESTAURANT </p>
                <Input
                    label=""
                    name="nbTitresRestaurant"
                    type="number"
                    value={formDataSimulator.nbTitresRestaurant || ""}
                    onChange={handleChange} 
                    clasName="col-span-1"
                />
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <Input
                        label=""
                        name="montantSalarialTitresRestaurant"
                        type="number"
                        value={formatEuroValue(formDataSimulator.montantSalarialTitresRestaurant)}
                        onChange={handleChange}
                        clasName="col-span-1"
                        suffix="€"
                        />
                    <Input
                        label=""
                        name="montantSalarialAutoTitresRestaurant"
                        type="number"
                        value={formatEuroValue(formDataSimulator.montantSalarialAutoTitresRestaurant)}
                        onChange={handleChange}
                        clasName="col-span-1"
                        disabled={true}
                        suffix="€"
                        />
                </div>
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <Input
                        label=""
                        name="montantPatronalTitresRestaurant"
                        type="number"
                        value={formatEuroValue(formDataSimulator.montantPatronalTitresRestaurant)}
                        onChange={handleChange}
                        clasName="col-span-1"
                        suffix="€"
                        />
                    <Input
                        label=""
                        name="montantPatronalAutoTitresRestaurant"
                        type="number"
                        value={formatEuroValue(formDataSimulator.montantPatronalAutoTitresRestaurant)}
                        onChange={handleChange}
                        clasName="col-span-1"
                        disabled={true}
                        suffix="€"
                        />
                </div>
            </div>
            <div className='grid grid-cols-5 gap-4 w-full items-center'>
                <p className="text-sm text-grey col-span-2">PANIERS </p>
                <Input
                    label=""
                    name="nbPaniers"
                    type="number"
                    value={formDataSimulator.nbPaniers || ""}
                    onChange={handleChange} 
                    clasName="col-span-1"
                />
                <div className='flex items-center gap-2 w-full col-span-1'>
                    <Input
                        label=""
                        name="montantSalarialPaniers"
                        type="number"
                        value={formatEuroValue(formDataSimulator.montantSalarialPaniers)}
                        onChange={handleChange}
                        clasName="col-span-1"
                        suffix="€"
                        />
                    <Input
                        label=""
                        name="montantSalarialAutoPaniers"
                        type="number"
                        value={formatEuroValue(formDataSimulator.montantSalarialAutoPaniers)}
                        onChange={handleChange}
                        clasName="col-span-1"
                        disabled={true}
                        suffix="€"
                        />
                </div>
            </div>

            <div className='grid grid-cols-5 gap-4 w-full items-center'>
                <p className="text-sm text-grey col-span-2">5% MONEGASQUE </p>
                <div className='col-span-1'></div>
                <div className='col-span-1'></div>

                <Input
                    label=""
                    name="montant5Monegasque"
                    type="number"
                    disabled={true}
                    value={formatEuroValue(formDataSimulator.montant5Monegasque)}
                    onChange={handleChange}
                    clasName="col-span-1"
                    suffix="€"
                />
            </div>

            <div className='grid grid-cols-5 gap-4 w-full items-center'>
                <p className="text-sm text-grey col-span-2">Avantage en nature </p>
                <div className='col-span-1'></div>
                <div className='col-span-1'></div>

                <Input
                    label=""
                    name="avantageEnNaturePatronale"
                    type="number"
                    disabled={true}
                    value={formatEuroValue(formDataSimulator.avantageEnNaturePatronale)}
                    onChange={handleChange}
                    clasName="col-span-1"
                    suffix="€"
                />
            </div>

            <div className='grid grid-cols-5 gap-4 w-full items-center'>
                <p className="text-sm text-grey col-span-2">REMBOURSEMENT TRANSPORT</p>
                <div className='col-span-1'></div>
                <div className='col-span-1'></div>

                <Input
                    label=""
                    name="remboursementTransportPatronale"
                    type="number"
                    value={formatEuroValue(formDataSimulator.remboursementTransportPatronale)}
                    onChange={handleChange}
                    clasName="col-span-1"
                    suffix="€"
                />
            </div>

            <div className='grid grid-cols-5 gap-4 w-full items-center'>
                <p className="text-sm text-grey col-span-2">INDEMNITÉ DE TÉLÉTRAVAIL</p>
                <div className='col-span-1'></div>
                <div className='col-span-1'></div>

                <Input
                    label=""
                    name="indemniteTeletravail"
                    type="number"
                    value={formatEuroValue(formDataSimulator.indemniteTeletravail)}
                    onChange={handleChange}
                    clasName="col-span-1"
                    suffix="€"
                />
            </div>

            <div className='grid grid-cols-5 gap-4 w-full items-center'>
                <p className="text-sm text-grey col-span-2">FRAIS/INDEMNITÉS DIVERSES</p>
                <div className='col-span-1'></div>
                <div className='col-span-1'></div>

                <Input
                    label=""
                    name="fraisDiversesPatronale"
                    type="number"
                    value={formatEuroValue(formDataSimulator.fraisDiversesPatronale)}
                    onChange={handleChange}
                    clasName="col-span-1"
                    suffix="€"
                />
            </div>

            <div className="grid grid-cols-5 gap-4 w-full items-center bg-yellow-500/20 p-2 mt-4 rounded-xl">
                <div className='col-span-1'></div>
                <div className='col-span-1'></div>
                <div className='col-span-1'></div>
                <p className='text-md text-grey font-bold col-span-2 text-end'>
                    {isNetMode ? 'BRUT' : 'NET'} :{' '}
                    <span className='text-green-800 font-bold ml-2'>
                        {formatComptableMaisPasTrop(
                            (isNetMode
                                ? formDataSimulator.totalGain
                                : formDataSimulator.totalNet
                            )?.toFixed(2) || "0.00"
                        )} €
                    </span>
                </p>
            </div>
            <div className="grid grid-cols-5 gap-4 w-full items-center bg-yellow-500/20 p-2 mt-4 rounded-xl">
                <div className='col-span-1'></div>
                <div className='col-span-1'></div>
                <p className='text-md text-grey font-bold col-span-3 text-end'>COUT TOTAL MENSUEL EMPLOYEUR : <span className='text-green-800 font-bold ml-2'>{formatComptableMaisPasTrop(formDataSimulator.totalCoutBrut?.toFixed(2) || "0.00")} €</span></p>
            </div>
        </section>

        <section className="w-full border-2 border-blue-500/40 rounded-xl shadow-md bg-blue-500/10 p-6">
            <p className="text-sm text-grey font-bold text-center mb-4">RÉCAPITULATIF ANNUEL</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex flex-col items-center justify-center bg-white/80 rounded-xl p-4 border border-gray-100">
                    <p className="text-xs text-grey font-bold uppercase tracking-wide mb-2">Brut annuel</p>
                    <p className="text-lg text-green-800 font-bold">{formatComptableMaisPasTrop(annuelBrut)} €</p>
                </div>
                <div className="flex flex-col items-center justify-center bg-white/80 rounded-xl p-4 border border-gray-100">
                    <p className="text-xs text-grey font-bold uppercase tracking-wide mb-2">Net annuel</p>
                    <p className="text-lg text-green-800 font-bold">{formatComptableMaisPasTrop(annuelNet)} €</p>
                </div>
                <div className="flex flex-col items-center justify-center bg-white/80 rounded-xl p-4 border border-gray-100">
                    <p className="text-xs text-grey font-bold uppercase tracking-wide mb-2">Coût employeur annuel</p>
                    <p className="text-lg text-green-800 font-bold">{formatComptableMaisPasTrop(annuelEmployeur)} €</p>
                </div>
            </div>
        </section>
    </div>
  )
}

export default Simulator